import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { usePipeline } from '../../context/PipelineContext';
import Modal from 'react-modal';
import { MdAdminPanelSettings, MdCreateNewFolder, MdOpenInNew, MdOutlineFileUpload, MdTune, MdDashboard, MdContentCopy, MdGridView, MdBalance, MdShare } from 'react-icons/md';
import { FaPlay, FaStop, FaTrash, FaSignOutAlt, FaUserCircle } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import {
  clearActivePipelineContext,
  copyPipeline,
  createNewPipeline,
  deletePipeline,
  exportSummaryPdf,
  getPipelineByName,
  listPipelineShares,
  listPipelines,
  revokePipelineShare,
  runPipelineByName,
  setActivePipelineContext,
  sharePipeline,
  view_node_ip_op,
} from '../../api/api';
import { toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Header.css';
import UploadModal from '../UploadModal/UploadModal';
import SetupUtility from '../SetupUtility/SetupUtility';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import BillingAnalyticsSection from './BillingAnalyticsSection';
import SourceSettlementSection from './SourceSettlementSection';
import { resolveExistingSourceMetrics } from './existingSourceMetrics';
import {
  computeBillingAnalytics,
  extractPersistableBillingAnalyticsInputs,
} from './billingAnalyticsShared';
import {
  buildBatteryTodRows,
  buildContiguousTodBuckets,
  summarizeBatteryTodRows,
} from './batteryTodShared';
import { notifyError } from '../../errors/notifyError';
import { OP } from '../../api/operations';
import { filterPipelines, pipelineItemIdentity, selectionAfterSearch } from './pipelineListSearch';

Modal.setAppElement('#root'); // Ensure this matches your root element
ChartJS.register(...registerables);

const TOTAL_BANKING_UNIT_VALUE = '__total__';
const BANKING_ROUND_PROPOSED = 'proposed';
const BANKING_ROUND_EXISTING_OA = 'existing_oa';
const BANKING_ROUND_TOTAL = 'total';

// The proposal round's real settlement policy lives on the Banking node's
// `banking_type` (e.g. "policy_banking_TN_2025" from BankingConfigModal),
// not the consumption node's `consumption_banking_policy` field — that field
// only reshapes monthly-slot consumption into hourly rows and is left at its
// unused default for hourly_csv inputs. Strip the "policy_banking_" prefix to
// land in the same "TN_2025"-style namespace as existing_oa_banking_policy.
const normalizeBankingPolicyName = (value) => (
  String(value || '').trim().replace(/^policy_banking_/, '')
);

// Mirrors the backend's Existing OA policy resolution (see
// build_hourly_consumption_from_multi_unit_slot_monthly in node_funcs.py) so
// the mismatch check below reflects what actually gets banked, not just the
// single-unit catalog fields (which multi-unit consumption nodes don't use).
const resolveExistingOaBankingPolicies = (catalog, nodeName) => {
  if (!catalog || !nodeName) return [];
  const mode = String(catalog[`params:consumption_input_mode_${nodeName}`] || '').trim().toLowerCase();
  const topLevelPolicy = catalog[`params:consumption_banking_policy_${nodeName}`] || '';

  if (mode === 'multi_unit_slot_monthly') {
    const raw = catalog[`params:slot_monthly_data_${nodeName}`];
    let units = [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
      units = Array.isArray(parsed?.units) ? parsed.units : [];
    } catch {
      units = [];
    }
    const oaSet = new Set();
    units.forEach((unit) => {
      const unitPolicy = unit?.banking_policy_name || topLevelPolicy || '';
      const oaPolicy = unit?.existing_oa_banking_policy || unitPolicy;
      if (oaPolicy) oaSet.add(oaPolicy);
    });
    return Array.from(oaSet).sort();
  }

  const oaPolicy = catalog[`params:existing_oa_banking_policy_${nodeName}`] || topLevelPolicy || '';
  return oaPolicy ? [oaPolicy] : [];
};

const Header = ({ currentUser, onSignOut, onOpenAdminOps, onOpenScenarioAnalysis, onOpenMultiUnitOptimization, onOpenWorkspace, activePage }) => {
  const { session, initialize_pipeline, run_pipeline } = usePipeline();
  const [pipelineName, setPipelineName] = useState('Unsaved_Pipeline');
  const [isRunning, setIsRunning] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isOpenModalOpen, setIsOpenModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [creationMessage, setCreationMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [copyIsError, setCopyIsError] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineSearch, setPipelineSearch] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [financeOutput, setFinanceOutput] = useState(null);
  const [opCostsOutput, setOpCostsOutput] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [annualMetrics, setAnnualMetrics] = useState(null);
  const [multiUnitSummary, setMultiUnitSummary] = useState(null);
  const [activeSummaryTab, setActiveSummaryTab] = useState('overview'); // 'overview' | 'visualizations' | 'assumptions' | 'billing'
  const [includeBillingAnalyticsInPdf, setIncludeBillingAnalyticsInPdf] = useState(true);
  const [includeOperationalCostInPdf, setIncludeOperationalCostInPdf] = useState(true);
  const [includeTodWiseTariffInPdf, setIncludeTodWiseTariffInPdf] = useState(true);
  const [step3Monthly, setStep3Monthly] = useState({ columns: [], rows: [] });
  const [bankingSummary, setBankingSummary] = useState({ tod_summary: [], monthly_summary: [] });
  const [selectedBankingSummaryUnit, setSelectedBankingSummaryUnit] = useState(TOTAL_BANKING_UNIT_VALUE);
  const [existingOaBankingSummary, setExistingOaBankingSummary] = useState({ available: false, tod_summary: [], monthly_summary: [] });
  const [sourceSettlementSummary, setSourceSettlementSummary] = useState({ available: false, sources: [] });
  const [selectedBankingRound, setSelectedBankingRound] = useState(BANKING_ROUND_PROPOSED);
  // Policy names behind the proposal round and the Existing OA round (each a
  // list, since a multi-unit consumption node can mix policies per unit),
  // used to detect a genuine banking-policy mismatch before combining TOD totals.
  const [bankingPolicyNames, setBankingPolicyNames] = useState({ proposed: [], existingOa: [] });

  // Existing OA / Proposed / Total figures for the banking-derived annual
  // summary cards (Banked units used, Net grid units, Units from OA, Lapsed
  // units) and their PDF-export mirror. Declared early (before the PDF
  // export builder further down) so both can read it via closure.
  const bankingCardFigures = useMemo(() => {
    const proposed = {
      bankedUnitsUsed: annualMetrics?.bankedUnitsUsed || 0,
      netGridUnits: annualMetrics?.netGridUnits || 0,
      unitsFromOA: annualMetrics?.unitsFromOA || 0,
      lapsedUnits: annualMetrics?.lapsedUnits || 0,
    };
    const existingOa = {
      bankedUnitsUsed: annualMetrics?.existingOaBankedUnits || 0,
      netGridUnits: annualMetrics?.existingOaNetLeftoverUnits || 0,
      unitsFromOA: annualMetrics?.existingOaServedUnits || 0,
      lapsedUnits: annualMetrics?.existingOaLapsedUnits || 0,
    };
    if (selectedBankingRound === BANKING_ROUND_EXISTING_OA) return existingOa;
    if (selectedBankingRound === BANKING_ROUND_TOTAL) {
      return {
        bankedUnitsUsed: proposed.bankedUnitsUsed + existingOa.bankedUnitsUsed,
        // The proposal round's own unmet demand is already downstream of
        // Existing OA settlement, so Total's "Net grid units" is the
        // proposal figure itself, not a sum of both rounds' leftovers.
        netGridUnits: proposed.netGridUnits,
        unitsFromOA: proposed.unitsFromOA + existingOa.unitsFromOA,
        lapsedUnits: proposed.lapsedUnits + existingOa.lapsedUnits,
      };
    }
    return proposed;
  }, [annualMetrics, selectedBankingRound]);

  const [projectAssumptions, setProjectAssumptions] = useState({ columns: [], rows: [] });
  const [globalParams, setGlobalParams] = useState({});
  const [financeInputs, setFinanceInputs] = useState(null);
  const [billingAnalyticsParams, setBillingAnalyticsParams] = useState({});
  const [billingAnalyticsParamsLoaded, setBillingAnalyticsParamsLoaded] = useState(false);
  const [batteryTodContext, setBatteryTodContext] = useState({
    policy: null,
    consumptionRows: [],
    bankingRows: [],
    batteryRows: [],
  });
  const [selectedSlot, setSelectedSlot] = useState('');
  const [copyPipelineName, setCopyPipelineName] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [shareMode, setShareMode] = useState('collaborate');
  const [shareMessage, setShareMessage] = useState('');
  const [shareIsError, setShareIsError] = useState(false);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [pipelineShares, setPipelineShares] = useState([]);

  
  // Derive likely node names for finance and operational costs from session
  const financeNodeName = useMemo(() => {
    const node = (session.nodes || []).find(n => n.node_type === 'finance');
    return node ? node.node_name : null;
  }, [session.nodes]);

  const opCostsNodeName = useMemo(() => {
    const node = (session.nodes || []).find(n => n.node_type === 'operationalcosts');
    return node ? node.node_name : null;
  }, [session.nodes]);

  const handleBillingAnalyticsParamsChange = useCallback((nextParams) => {
    setBillingAnalyticsParams((prev) => {
      const merged = { ...(prev || {}), ...(nextParams || {}) };
      if (!nextParams || !Object.prototype.hasOwnProperty.call(nextParams, 'batteryTodTariffsByBlock')) {
        merged.batteryTodTariffsByBlock = prev?.batteryTodTariffsByBlock || {};
      }
      return JSON.stringify(prev || {}) === JSON.stringify(merged) ? prev : merged;
    });
  }, []);

  const billingAnalyticsSnapshot = useMemo(() => {
    if (!billingAnalyticsParamsLoaded) return null;
    const persistedInputs = extractPersistableBillingAnalyticsInputs(billingAnalyticsParams || {});
    if (!persistedInputs.wasCalculated) return null;
    return computeBillingAnalytics({
      inputs: persistedInputs,
      financeInputs,
      globalParams,
      annualMetrics,
      bankingSummary,
      opCostsOutput,
      savedInputs: persistedInputs,
    });
  }, [
    annualMetrics,
    bankingSummary,
    billingAnalyticsParams,
    billingAnalyticsParamsLoaded,
    financeInputs,
    globalParams,
    opCostsOutput,
  ]);

  const batteryTodBuckets = useMemo(
    () => buildContiguousTodBuckets(batteryTodContext.policy),
    [batteryTodContext.policy],
  );
  const batteryTodTariffs = useMemo(
    () => billingAnalyticsParams?.batteryTodTariffsByBlock || {},
    [billingAnalyticsParams?.batteryTodTariffsByBlock],
  );
  const batteryTodRows = useMemo(() => buildBatteryTodRows({
    buckets: batteryTodBuckets,
    consumptionRows: batteryTodContext.consumptionRows,
    bankingRows: batteryTodContext.bankingRows,
    batteryRows: batteryTodContext.batteryRows,
    tariffs: batteryTodTariffs,
  }), [
    batteryTodBuckets,
    batteryTodContext.bankingRows,
    batteryTodContext.batteryRows,
    batteryTodContext.consumptionRows,
    batteryTodTariffs,
  ]);
  const batteryTodTotals = useMemo(() => summarizeBatteryTodRows(batteryTodRows), [batteryTodRows]);
  const batteryTodTariffsComplete = batteryTodRows.length > 0 && batteryTodRows.every((row) => row.hasTariff);

  const handleBatteryTodTariffChange = useCallback((key, value) => {
    setBillingAnalyticsParams((prev) => ({
      ...(prev || {}),
      batteryTodTariffsByBlock: {
        ...(prev?.batteryTodTariffsByBlock || {}),
        [key]: value,
      },
    }));
  }, []);

  const persistBatteryTodTariffs = useCallback(async () => {
    if (!session.pipeline_name || session.pipeline_name === 'Unsaved_Pipeline') return;
    try {
      const { setBillingAnalyticsParams: persistBillingParams } = await import('../../api/api');
      await persistBillingParams(
        session.pipeline_name,
        extractPersistableBillingAnalyticsInputs(billingAnalyticsParams || {}),
      );
    } catch (error) {
      notifyError(error, { operation: OP.params.setBillingAnalytics });
    }
  }, [billingAnalyticsParams, session.pipeline_name]);

  const openSummary = () => {
    if (!session.pipeline_name || session.pipeline_name === 'Unsaved_Pipeline') {
      toast.info('Open or run a pipeline to view project summary', { position: 'bottom-right', transition: Bounce });
      return;
    }
    if (!financeNodeName || !opCostsNodeName) {
      toast.info('Add Finance and Operational Costs nodes to view project summary', { position: 'bottom-right', transition: Bounce });
    }
    setSelectedBankingSummaryUnit(TOTAL_BANKING_UNIT_VALUE);
    setBillingAnalyticsParamsLoaded(false);
    setIsSummaryOpen(true);
  };

  function buildMultiUnitSummaryFromNode(nodeResponse) {
    const outputs = Array.isArray(nodeResponse?.additional_outputs) ? nodeResponse.additional_outputs : [];
    const findOutputRows = (label) => {
      const match = outputs.find((output) => String(output?.label || '').toLowerCase() === label.toLowerCase());
      return Array.isArray(match?.rows) ? match.rows : [];
    };
    const allocationRows = findOutputRows('Unit Allocation Summary');
    const preBankingRows = findOutputRows('Unit Pre-Banking Summary');
    const postBankingRows = findOutputRows('Unit Post-Banking Summary');
    const portfolioRows = findOutputRows('Portfolio Summary');
    const portfolio = portfolioRows[0] || null;
    if (!allocationRows.length && !postBankingRows.length && !portfolio) return null;
    return {
      allocationRows,
      preBankingRows,
      postBankingRows,
      portfolio,
      unitRows: postBankingRows.length ? postBankingRows : preBankingRows,
    };
  }

  // Fetch node outputs when summary opens
  useEffect(() => {
    const fetchSummary = async () => {
      if (!isSummaryOpen) return;
      if (!session.pipeline_name) return;
      const summaryRunId = session.last_run_id || null;
      setSummaryLoading(true);
      setSummaryError('');
      setBillingAnalyticsParamsLoaded(false);
      try {
        const requests = [];
        if (financeNodeName) requests.push(view_node_ip_op(session.pipeline_name, financeNodeName, summaryRunId)); else requests.push(Promise.resolve(null));
        if (opCostsNodeName) requests.push(view_node_ip_op(session.pipeline_name, opCostsNodeName, summaryRunId)); else requests.push(Promise.resolve(null));

        // Identify relevant nodes
        const nodes = session.nodes || [];
        const capperNode = nodes.find(n => n.node_type === 'capper')?.node_name;
        const lossNode = nodes.find(n => n.node_type === 'loss')?.node_name;
        const consumptionNode = nodes.find(n => n.node_type === 'consumption')?.node_name;
        const bankingNode = nodes.find(n => n.node_type === 'banking')?.node_name;
        const batteryNode = nodes.find(n => n.node_type === 'battery')?.node_name;
        const scalerNodes = nodes.filter(n => n.node_type === 'scaler').map(n => n.node_name);

        const metricReqs = [];
        if (capperNode) metricReqs.push(view_node_ip_op(session.pipeline_name, capperNode, summaryRunId)); else metricReqs.push(Promise.resolve(null));
        if (lossNode) metricReqs.push(view_node_ip_op(session.pipeline_name, lossNode, summaryRunId)); else metricReqs.push(Promise.resolve(null));
        if (consumptionNode) metricReqs.push(view_node_ip_op(session.pipeline_name, consumptionNode, summaryRunId)); else metricReqs.push(Promise.resolve(null));
        if (bankingNode) metricReqs.push(view_node_ip_op(session.pipeline_name, bankingNode, summaryRunId)); else metricReqs.push(Promise.resolve(null));
        if (batteryNode) metricReqs.push(view_node_ip_op(session.pipeline_name, batteryNode, summaryRunId)); else metricReqs.push(Promise.resolve(null));
        // Fetch scaler nodes (at most 2 typical: wind, solar)
        const scalerPromises = scalerNodes.map(nm => view_node_ip_op(session.pipeline_name, nm, summaryRunId).catch(() => null));

        // Also fetch analysis-derived summaries
        // Lazy import from api to avoid circulars
        const { getAnalysisLapsedSum, getAnalysisStep3Monthly, getBankingSummary, getExistingOaBankingSummary, getProjectAssumptions, getGlobalParams, getBillingAnalyticsParams, getBankingPolicySlots, getSourceSettlementSummary } = await import('../../api/api');

        const [
          finRes,
          opRes,
          capperRes,
          lossRes,
          consRes,
          bankRes,
          batteryRes,
          scalerResArr,
          analysisLapsed,
          step3,
          bankingSumm,
          existingOaBankingSumm,
          assumptionsRes,
          gpRes,
          baRes,
          pipelineDoc,
          policyResponse,
          settleRes,
        ] = await Promise.all([
          ...requests,
          ...metricReqs,
          Promise.all(scalerPromises),
          getAnalysisLapsedSum(session.pipeline_name, summaryRunId).catch(() => ({ lapsed_sum: 0 })),
          getAnalysisStep3Monthly(session.pipeline_name, summaryRunId).catch(() => ({ columns: [], rows: [] })),
          getBankingSummary(session.pipeline_name, summaryRunId).catch(() => ({ tod_summary: [], monthly_summary: [] })),
          getExistingOaBankingSummary(session.pipeline_name, summaryRunId).catch(() => ({ available: false, tod_summary: [], monthly_summary: [] })),
          getProjectAssumptions(session.pipeline_name, summaryRunId).catch(() => ({ columns: [], rows: [] })),
          getGlobalParams(session.pipeline_name).catch(() => ({})),
          getBillingAnalyticsParams(session.pipeline_name).catch(() => ({})),
          getPipelineByName(session.pipeline_name).catch(() => ({})),
          getBankingPolicySlots().catch(() => ({ policies: [] })),
          getSourceSettlementSummary(session.pipeline_name, summaryRunId).catch(() => ({ available: false, sources: [] })),
        ]);
        setFinanceOutput(finRes && finRes.node_output_df ? finRes.node_output_df : null);
        setOpCostsOutput(opRes && opRes.node_output_df ? opRes.node_output_df : null);
        setStep3Monthly(step3);
        setProjectAssumptions(assumptionsRes || { columns: [], rows: [] });
        setGlobalParams(gpRes?.GLOBAL_PARAMS || gpRes || {});
        setBillingAnalyticsParams(baRes || {});
        setBillingAnalyticsParamsLoaded(true);

        // Pull finance node inputs (X/A-F/G + rebate components) from pipeline catalog (per-node params)
        try {
          if (financeNodeName) {
            const catalog = (pipelineDoc && pipelineDoc.catalog) || {};
            const k = (suffix) => `params:${suffix}_${financeNodeName}`;
            const readNumOrNull = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
            setFinanceInputs({
              discomTariffCalcMode: (catalog[k('discom_tariff_calc_mode')] ?? 'standard'),
              unitsX: readNumOrNull(catalog[k('units_X')]),
              energyCostA: readNumOrNull(catalog[k('energy_cost_A')]),
              wheelingChargeB: readNumOrNull(catalog[k('wheeling_charge_B')]),
              electricityDutyC: readNumOrNull(catalog[k('electricity_duty_C')]),
              todChargesD: readNumOrNull(catalog[k('tod_charges_D')]),
              taxE: readNumOrNull(catalog[k('tax_E')]),
              facF: readNumOrNull(catalog[k('fac_F')]),
              rebateG: readNumOrNull(catalog[k('rebate_G')]),
              ghEnergyCharge: readNumOrNull(catalog[k('gh_energy_charge')]),
              ghFuelSurcharge: readNumOrNull(catalog[k('gh_fuel_surcharge')]),
              ghPeakSurcharge: readNumOrNull(catalog[k('gh_peak_surcharge')]),
              ghSolarPeakDiscount: readNumOrNull(catalog[k('gh_solar_peak_discount')]),
              ghPfIncentive: readNumOrNull(catalog[k('gh_pf_incentive')]),
              ghEhvRebate: readNumOrNull(catalog[k('gh_ehv_rebate')]),
              ghDemandCharges: readNumOrNull(catalog[k('gh_demand_charges')]),
              ghElectricityDuty: readNumOrNull(catalog[k('gh_electricity_duty')]),
              bulkConsumptionRebate: readNumOrNull(catalog[k('bulk_consumption_rebate')]),
              promptPaymentDiscount: readNumOrNull(catalog[k('prompt_payment_discount')]),
              incrementalConsumptionRebate: readNumOrNull(catalog[k('incremental_consumption_rebate')]),
              loadFactorIncentive: readNumOrNull(catalog[k('load_factor_incentive')]),
              universalSubsidy: readNumOrNull(catalog[k('universal_subsidy')]),
              universalSubsidyLabel: String(catalog[k('universal_subsidy_label')] || '').trim() || 'Universal subsidy',
            });
          } else {
            setFinanceInputs(null);
          }
        } catch (e) {
          setFinanceInputs(null);
        }

        // Helper to get sum of last column from response
        const sumLastColumn = (res) => {
          if (!res || !res.node_output_df || !Array.isArray(res.node_output_df) || res.node_output_df.length === 0) return 0;
          const cols = Object.keys(res.node_output_df[0]);
          if (!cols || cols.length === 0) return 0;
          const lastCol = cols[cols.length - 1];
          const sumMap = res.sum || {};
          const val = Number(sumMap[lastCol]);
          return isFinite(val) ? val : 0;
        };

        const sumNamedColumn = (res, columnName) => {
          const sumValue = Number(res?.sum?.[columnName]);
          if (isFinite(sumValue)) return sumValue;
          const rows = Array.isArray(res?.node_output_df) ? res.node_output_df : [];
          return rows.reduce((total, row) => {
            const value = Number(row?.[columnName]);
            return total + (isFinite(value) ? value : 0);
          }, 0);
        };

        // 1) and 2) Scaler nodes classification.
        // Use scaler outputs for total annual generation so the summary reflects
        // pre-curtailment generation instead of capper output.
        let totalAnnualGeneration = 0;
        let totalWindGeneration = 0;
        let totalSolarGeneration = 0;
        let hasScalerGeneration = false;
        (scalerResArr || []).forEach(res => {
          if (!res || !res.node_output_df || res.node_output_df.length === 0) return;
          const cols = Object.keys(res.node_output_df[0]);
          const lastCol = cols[cols.length - 1] || '';
          const lastColLower = (lastCol || '').toLowerCase();
          const sumMap = res.sum || {};
          const s = Number(sumMap[lastCol]) || 0;
          totalAnnualGeneration += s;
          hasScalerGeneration = true;
          if (lastColLower.includes('wind')) totalWindGeneration += s;
          else if (lastColLower.includes('solar') || lastColLower.includes('pv')) totalSolarGeneration += s;
        });
        if (!hasScalerGeneration) {
          totalAnnualGeneration = sumLastColumn(capperRes);
        }
        // Portfolio curtailment at the capper. Scaler totals above stay pre-curtailment.
        const totalCurtailedUnits = sumNamedColumn(capperRes, 'Curtailment');

        // 3) RE supplied units from loss subtractor (last column sum)
        const reSuppliedUnits = sumLastColumn(lossRes);

        // 4) Consumption after existing RE is the proposal balance; named columns preserve the original mix.
        const consumptionBalance = sumLastColumn(consRes);
        const existingBtmUnits = sumNamedColumn(consRes, 'Existing BTM (kWh)');
        // Existing OA figures come from the Consumption node, or are summed across every source
        // when a Settle chain settled them (then the balance is the last source's residual).
        const {
          balanceAnnualConsumption,
          originalAnnualConsumption,
          existingOaReUnits,
          existingOaBankedUnits, // audit columns are 0 when the Existing OA banking round was not enabled
          existingOaLapsedUnits,
          existingOaServedUnits,
        } = resolveExistingSourceMetrics(settleRes, {
          balanceAnnualConsumption: consumptionBalance,
          originalAnnualConsumption: sumNamedColumn(consRes, 'Original Hourly Consumption (kWh)') || consumptionBalance,
          existingOaReUnits: sumNamedColumn(consRes, 'Existing OA RE (kWh)'),
          existingOaBankedUnits: sumNamedColumn(consRes, 'Existing OA Banked (kWh)'),
          existingOaLapsedUnits: sumNamedColumn(consRes, 'Existing OA Lapsed (kWh)'),
        });

        // 5) Hourly units banked from analysis CSV
        const hourlyUnitsBanked = Number(analysisLapsed?.lapsed_sum) || 0;

        // 6-9) From banking node sums by specific columns
        const bankingSums = bankRes?.sum || {};
        const lapsedUnits = Number(bankingSums['Surplus Generation']) || 0;
        const bankedUnitsUsed = Number(bankingSums['Total Banking settlement']) || 0;
        const netGridUnits = Number(bankingSums['Unmet Demand']) || 0;
        const unitsFromOA = Number(bankingSums['Net Settled Units']) || 0;
        const proposedReUnits = Math.max(unitsFromOA, 0);
        const pctOfOriginal = (value) => (
          originalAnnualConsumption > 0 ? (Number(value || 0) / originalAnnualConsumption) * 100 : 0
        );
        const batteryChargingUnits = sumNamedColumn(
          batteryRes,
          'Units for battery charging'
        ) || sumNamedColumn(
          batteryRes,
          'Units for battery charging during off peak slot- 9 to 17'
        );
        const batteryDischargingUnits = sumNamedColumn(
          batteryRes,
          'Battery Discharge unit considering inefficiency'
        );

        // Use backend-provided banking summaries directly
        setBankingSummary(bankingSumm || { tod_summary: [], monthly_summary: [] });
        setExistingOaBankingSummary(existingOaBankingSumm || { available: false, tod_summary: [], monthly_summary: [] });
        setSourceSettlementSummary(settleRes || { available: false, sources: [] });
        setSelectedBankingSummaryUnit(TOTAL_BANKING_UNIT_VALUE);
        setSelectedBankingRound(BANKING_ROUND_PROPOSED);
        setMultiUnitSummary(buildMultiUnitSummaryFromNode(bankRes));

        setAnnualMetrics({
          totalAnnualGeneration,
          totalWindGeneration,
          totalSolarGeneration,
          totalCurtailedUnits,
          reSuppliedUnits,
          totalAnnualConsumption: originalAnnualConsumption,
          originalAnnualConsumption,
          balanceAnnualConsumption,
          existingOaReUnits,
          existingBtmUnits,
          proposedReUnits,
          existingOaRePct: pctOfOriginal(existingOaReUnits),
          existingBtmPct: pctOfOriginal(existingBtmUnits),
          proposedRePct: pctOfOriginal(proposedReUnits),
          overallReUnits: existingOaReUnits + existingBtmUnits + proposedReUnits,
          overallRePct: pctOfOriginal(existingOaReUnits + existingBtmUnits + proposedReUnits),
          hourlyUnitsBanked,
          lapsedUnits,
          bankedUnitsUsed,
          netGridUnits,
          unitsFromOA,
          existingOaBankedUnits,
          existingOaLapsedUnits,
          existingOaServedUnits,
          existingOaNetLeftoverUnits: balanceAnnualConsumption,
          batteryChargingUnits,
          batteryDischargingUnits,
        });
        const catalog = pipelineDoc?.catalog || {};
        const policyName = consumptionNode
          ? catalog[`params:consumption_banking_policy_${consumptionNode}`]
          : null;
        const proposedBankingType = bankingNode
          ? catalog[`params:banking_type_${bankingNode}`]
          : null;
        const normalizedProposedPolicy = normalizeBankingPolicyName(proposedBankingType);
        setBankingPolicyNames({
          proposed: normalizedProposedPolicy ? [normalizedProposedPolicy] : [],
          existingOa: consumptionNode ? resolveExistingOaBankingPolicies(catalog, consumptionNode) : [],
        });
        const policy = (policyResponse?.policies || []).find((item) => item.policy_name === policyName) || null;
        setBatteryTodContext({
          policy,
          consumptionRows: Array.isArray(consRes?.node_output_df) ? consRes.node_output_df : [],
          bankingRows: Array.isArray(bankRes?.node_output_df) ? bankRes.node_output_df : [],
          batteryRows: Array.isArray(batteryRes?.node_output_df) ? batteryRes.node_output_df : [],
        });
      } catch (e) {
        setSummaryError(e?.message || 'Failed to load summary');
        setBillingAnalyticsParamsLoaded(true);
        setMultiUnitSummary(null);
        setBatteryTodContext({ policy: null, consumptionRows: [], bankingRows: [], batteryRows: [] });
        setBankingPolicyNames({ proposed: [], existingOa: [] });
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
  }, [isSummaryOpen, session.pipeline_name, session.last_run_id, session.nodes, financeNodeName, opCostsNodeName]);

  // Helpers to parse finance and operational outputs
  const format2 = (val) => {
    const n = Number(val);
    if (!isFinite(n)) return val ?? '-';
    return n.toFixed(2);
  };
  const format1 = (val) => {
    const n = Number(val);
    if (!isFinite(n)) return val ?? '-';
    return n.toFixed(1);
  };
  const round2num = (val) => {
    const n = Number(val);
    if (!isFinite(n)) return 0;
    return Math.round(n * 100) / 100;
  };

  // Indian number formatter (with 2 decimals)
  const formatIndian = (value) => {
    const n = Number(value);
    if (!isFinite(n)) return value ?? '-';
    const parts = n.toFixed(2).split('.');
    const int = parts[0];
    const dec = parts[1];
    const lastThree = int.slice(-3);
    const other = int.slice(0, -3);
    const formatted = other !== '' ? other.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree : lastThree;
    return `${formatted}.${dec}`;
  };

  // Indian number formatter (whole numbers; no decimals)
  const formatIndian0 = (value) => {
    const n = Number(value);
    if (!isFinite(n)) return value ?? '-';
    const rounded = Math.round(n);
    const int = String(Math.trunc(rounded));
    const sign = int.startsWith('-') ? '-' : '';
    const abs = sign ? int.slice(1) : int;
    const lastThree = abs.slice(-3);
    const other = abs.slice(0, -3);
    const formattedAbs = other !== '' ? other.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree : lastThree;
    return `${sign}${formattedAbs}`;
  };
  const slugify = (text) =>
    (text || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');

  const formatPercent = (value) => {
    const n = Number(value);
    if (!isFinite(n)) return '-';
    return `${n.toFixed(1)}%`;
  };

  const formatPercent2 = (value) => {
    const n = Number(value);
    if (!isFinite(n)) return '-';
    return `${n.toFixed(2)}%`;
  };

  const defaultMonthlyBankingColumns = [
    { key: 'MonthName', label: 'Month', type: 'text' },
    { key: 'Consumption', label: 'Consumption', type: 'number' },
    { key: 'DemandMet', label: 'Demand met', type: 'number' },
    { key: 'SatisfiedPct', label: '% satisfied', type: 'percent' },
    { key: 'LapsedPct', label: 'Lapsed (%)', type: 'percent' },
    { key: 'LapsedUnits', label: 'Lapsed units', type: 'number' },
  ];

  const isMonthlyPooledBankingSummary = (summary) => summary?.monthly_summary_mode === 'monthly_pooled';

  const getMonthlyBankingRows = (summary) => {
    const rows = Array.isArray(summary?.monthly_summary) ? summary.monthly_summary : [];
    if (!isMonthlyPooledBankingSummary(summary)) return rows;

    return rows.map((row) => {
      const demandMet = Number(row?.['Net Settled Units']);
      const unmetDemand = Number(row?.['Unmet Demand']);
      const lapsedUnits = Number(row?.['Surplus Generation']);
      const normalizedDemandMet = Number.isFinite(demandMet) ? demandMet : 0;
      const normalizedUnmetDemand = Number.isFinite(unmetDemand) ? unmetDemand : 0;
      const normalizedLapsedUnits = Number.isFinite(lapsedUnits) ? lapsedUnits : 0;
      const consumption = normalizedDemandMet + normalizedUnmetDemand;
      const satisfiedPct = consumption > 0 ? (normalizedDemandMet / consumption) * 100 : 0;
      const lapsedPct = consumption > 0 ? (normalizedLapsedUnits / consumption) * 100 : 0;

      return {
        ...row,
        MonthName: row?.Month ?? row?.MonthName ?? '-',
        Consumption: consumption,
        DemandMet: normalizedDemandMet,
        SatisfiedPct: satisfiedPct,
        LapsedPct: lapsedPct,
        LapsedUnits: normalizedLapsedUnits,
      };
    });
  };

  const getTodBankingRows = (summary) => (
    Array.isArray(summary?.tod_summary) ? summary.tod_summary : []
  );

  const getMonthlyBankingColumns = (summary) => {
    if (isMonthlyPooledBankingSummary(summary)) return defaultMonthlyBankingColumns;
    const cols = Array.isArray(summary?.monthly_summary_columns) ? summary.monthly_summary_columns : [];
    return cols.length ? cols : defaultMonthlyBankingColumns;
  };

  const formatBankingSummaryCell = (value, type) => {
    if (type === 'number') return formatIndian0(value);
    if (type === 'percent') return formatPercent2(value);
    return value ?? '-';
  };

  const batteryTodGroupedChartData = {
    labels: batteryTodRows.map((row) => row.label),
    datasets: [
      {
        label: 'Grid cost without battery',
        data: batteryTodRows.map((row) => row.gridCostWithoutBattery || 0),
        backgroundColor: 'rgba(148, 163, 184, 0.75)',
        borderColor: '#64748b',
        borderWidth: 1,
      },
      {
        label: 'Grid cost with battery',
        data: batteryTodRows.map((row) => row.gridCostWithBattery || 0),
        backgroundColor: 'rgba(5, 150, 105, 0.78)',
        borderColor: '#047857',
        borderWidth: 1,
      },
    ],
  };
  const batteryTodStackedChartData = {
    labels: batteryTodRows.map((row) => row.label),
    datasets: [
      {
        label: 'Remaining grid cost',
        data: batteryTodRows.map((row) => row.gridCostWithBattery || 0),
        backgroundColor: 'rgba(5, 150, 105, 0.78)',
      },
      {
        label: 'Battery-replaced cost (savings)',
        data: batteryTodRows.map((row) => row.savings || 0),
        backgroundColor: 'rgba(250, 204, 21, 0.82)',
      },
    ],
  };
  const batteryTodRupeeChartOptions = (stacked = false) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } },
    scales: {
      x: { stacked, ticks: { maxRotation: 35, minRotation: 0 } },
      y: {
        stacked,
        beginAtZero: true,
        title: { display: true, text: 'Grid cost (INR)' },
        ticks: { callback: (value) => `₹${formatIndian0(value)}` },
      },
    },
  });

  const renderBatteryTodTab = () => (
    <>
      <div className="summary-section span-2">
        <h3 className="section-title">TOD-wise battery tariff replacement</h3>
        {!batteryTodRows.length ? (
          <div className="summary-empty">Run the Consumption, Banking, and Battery nodes with a banking policy to see policy-derived TOD blocks.</div>
        ) : (
          <>
            <div className="summary-table-wrapper">
              <table className="summary-table">
                <thead>
                  <tr>
                    <th>Time slot</th>
                    <th>Consumption</th>
                    <th>Demand met</th>
                    <th>Grid consumption</th>
                    <th>TOD tariff (₹/unit)</th>
                    <th>Battery discharge units</th>
                    <th>Savings (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {batteryTodRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td className="numeric-cell">{formatIndian(row.consumption)}</td>
                      <td className="numeric-cell">{formatIndian(row.demandMet)}</td>
                      <td className="numeric-cell">{formatIndian(row.gridConsumption)}</td>
                      <td className="numeric-cell battery-tod-tariff-cell">
                        <input
                          className="battery-tod-tariff-input"
                          type="number"
                          min="0"
                          step="0.01"
                          value={batteryTodTariffs[row.key] ?? ''}
                          onChange={(event) => handleBatteryTodTariffChange(row.key, event.target.value)}
                          onBlur={persistBatteryTodTariffs}
                          placeholder="Enter tariff"
                        />
                      </td>
                      <td className="numeric-cell">{formatIndian(row.batteryDischargeUnits)}</td>
                      <td className="numeric-cell">{row.hasTariff ? `₹${formatIndian(row.savings)}` : '-'}</td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td><strong>Total</strong></td>
                    <td className="numeric-cell"><strong>{formatIndian(batteryTodTotals.consumption)}</strong></td>
                    <td className="numeric-cell"><strong>{formatIndian(batteryTodTotals.demandMet)}</strong></td>
                    <td className="numeric-cell"><strong>{formatIndian(batteryTodTotals.gridConsumption)}</strong></td>
                    <td />
                    <td className="numeric-cell"><strong>{formatIndian(batteryTodTotals.batteryDischargeUnits)}</strong></td>
                    <td className="numeric-cell"><strong>{batteryTodTariffsComplete ? `₹${formatIndian(batteryTodTotals.savings)}` : '-'}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
            {!batteryTodTariffsComplete && (
              <div className="summary-empty battery-tod-prompt">Enter a TOD tariff for every policy time block to calculate savings and grid-cost charts.</div>
            )}
          </>
        )}
      </div>
      {batteryTodTariffsComplete && (
        <>
          <div className="summary-section span-2">
            <h3 className="section-title">Battery grid-cost impact</h3>
            <div className="cards-grid battery-tod-kpi-grid">
              <div className="summary-card"><div className="card-title">Grid cost without battery</div><div className="card-value">₹{formatIndian(batteryTodTotals.gridCostWithoutBattery)}</div></div>
              <div className="summary-card"><div className="card-title">Grid cost with battery</div><div className="card-value">₹{formatIndian(batteryTodTotals.gridCostWithBattery)}</div></div>
              <div className="summary-card"><div className="card-title">Total battery savings</div><div className="card-value">₹{formatIndian(batteryTodTotals.savings)}</div></div>
            </div>
          </div>
          <div className="summary-section">
            <h3 className="section-title">Grid cost with vs without battery</h3>
            <div className="summary-chart battery-tod-chart">
              <Bar data={batteryTodGroupedChartData} options={batteryTodRupeeChartOptions(false)} />
            </div>
          </div>
          <div className="summary-section">
            <h3 className="section-title">Remaining grid cost and replaced cost</h3>
            <div className="summary-chart battery-tod-chart">
              <Bar data={batteryTodStackedChartData} options={batteryTodRupeeChartOptions(true)} />
            </div>
          </div>
        </>
      )}
    </>
  );

  // Shared helpers 
  const coerceNum = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    return isFinite(n) ? n : "";
  };

  const parseSeries = (raw) => {
    
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      if (raw.__default__ != null && raw.__default__ !== '') raw = raw.__default__;
      else if (raw.__by_node__ && typeof raw.__by_node__ === 'object') {
        const vals = Object.values(raw.__by_node__);
        if (vals.length) raw = vals[0];
      }
    }
    if (Array.isArray(raw)) {
      const series = raw.map(x => Number(x)).filter(x => isFinite(x));
      return { scalar: series[0] ?? "", series: series.length > 1 ? series : [] };
    }
    if (typeof raw === 'string') {
      const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
      if (parts.length > 1) {
        const series = parts.map(p => Number(p)).filter(n => isFinite(n));
        return { scalar: series[0] ?? "", series: series.length > 1 ? series : [] };
      }
      const n = Number(raw);
      return { scalar: isFinite(n) ? n : "", series: [] };
    }
    const n = Number(raw);
    return { scalar: isFinite(n) ? n : "", series: [] };
  };

  const getBatterySummaryRows = () => {
    const cols = projectAssumptions?.columns || [];
    const rows = projectAssumptions?.rows || [];
    const labelCol = cols[0];
    if (!labelCol || !rows.length) return [];

    const sectionNames = new Set([
      "Project Details",
      "Funding Plan",
      "Loan Assumptions",
      "Other Assumptions",
      "Lease assumptions",
      "Annual Maintenance Assumptions (excl GST)",
      "Battery Summary",
      "Funding Plan - Equity 100%",
      "Debt - 75.0%",
      "Debt - 75%",
    ]);
    const startIdx = rows.findIndex((row) => String(row?.[labelCol] || '').trim() === 'Battery Summary');
    if (startIdx < 0) return [];

    const valueCols = cols.slice(1);
    const out = [];
    for (let i = startIdx + 1; i < rows.length; i++) {
      const row = rows[i] || {};
      const label = String(row[labelCol] || '').trim();
      if (!label) break;
      if (sectionNames.has(label)) break;
      const value = valueCols.map((col) => row[col]).find((v) => v !== null && v !== undefined && String(v).trim() !== '');
      out.push({ label, value: value ?? '' });
    }
    return out;
  };

  const renderBatterySummaryTable = () => {
    const rows = getBatterySummaryRows();
    if (!rows.length) return null;
    return (
      <div id="battery-summary" className="summary-section span-2">
        <h3 className="section-title">Battery Summary</h3>
        <div className="summary-table-wrapper">
          <table className="summary-table battery-summary-table">
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td className="numeric-cell">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  // --- Assumptions table renderer ---
  const renderAssumptionsTable = () => {
    const cols = projectAssumptions?.columns || [];
    const rows = projectAssumptions?.rows || [];
    if (!rows.length || !cols.length) {
      return <div className="summary-empty">Run pipeline to populate assumptions</div>;
    }

    const sectionNames = new Set([
      "Project Details",
      "Funding Plan",
      "Loan Assumptions",
      "Other Assumptions",
      "Lease assumptions",
      "Annual Maintenance Assumptions (excl GST)",
      "Battery Summary",
      "Funding Plan - Equity 100%",
      "Debt - 75.0%",
      "Debt - 75%",
    ]);

    const isSectionRow = (val) => {
      const v = (val || "").trim();
      if (!v) return false;
      return sectionNames.has(v);
    };
    const coerceGlobalNumber = (val, fallback = 0) => {
      if (val === null || val === undefined || val === '') return fallback;
      if (typeof val === 'number') return isFinite(val) ? val : fallback;
      if (typeof val === 'string') {
        const n = parseFloat(val);
        return isFinite(n) ? n : fallback;
      }
      if (typeof val === 'object') {
        if (val.__default__ !== null && val.__default__ !== undefined && val.__default__ !== '') {
          const n = parseFloat(val.__default__);
          if (isFinite(n)) return n;
        }
        if (val.__by_node__ && typeof val.__by_node__ === 'object') {
          const first = Object.values(val.__by_node__)[0];
          const n = parseFloat(first);
          if (isFinite(n)) return n;
        }
      }
      return fallback;
    };
    const coerceBool = (val) => {
      if (typeof val === 'boolean') return val;
      if (typeof val === 'number') return val !== 0;
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        return ['true', '1', 'yes', 'y'].includes(v);
      }
      return false;
    };
    const getCapperEvacuationLimitMw = () => {
      const capperNode = (session.nodes || []).find((n) => n.node_type === 'capper');
      if (!capperNode) return NaN;
      const params = capperNode?.node_params?.[0] || {};
      const manualCapLimit = coerceGlobalNumber(params.cap_limit, NaN);
      if (isFinite(manualCapLimit)) return manualCapLimit / 1000;

      const gp = globalParams || {};
      const windTurbines = coerceGlobalNumber(gp.mult_factor_wind ?? gp.no_of_wind_turbines, 0);
      const solarMw = coerceGlobalNumber(gp.mult_factor_solar ?? gp.no_of_solar_units, 0);
      const mwPerTurbine = coerceGlobalNumber(gp.mw_per_turbine, 2.1) || 2.1;
      const ratioRaw = coerceGlobalNumber(params.acdc_ratio ?? params.capping_limit, 1);
      const acdcRatio = ratioRaw === 0 ? 1 : ratioRaw;
      const loss = coerceGlobalNumber(params.gen_to_trans_loss, 0);
      const windMw = Math.round((windTurbines * mwPerTurbine) * 100) / 100;
      const effectiveSolarMw = coerceBool(gp.Btm_flag) ? 0 : Math.round(solarMw * 100) / 100;
      const lossFactor = 1 + loss;

      if (coerceBool(params.KA)) {
        return Math.max(windMw, effectiveSolarMw / acdcRatio) * lossFactor;
      }
      return (windMw + (effectiveSolarMw / acdcRatio)) * lossFactor;
    };
    const getProjectDetailTotalValue = (label, value) => {
      const key = String(label || '').trim().toLowerCase();
      if (key === 'no of turbines') return '-';
      if (key === 'mwps') {
        const evacuationLimitMw = getCapperEvacuationLimitMw();
        if (isFinite(evacuationLimitMw)) return format2(evacuationLimitMw);
      }
      return value;
    };

    // For assumptions rows we use first numeric across all value columns (wind/solar/RT/hybrid)
    const firstNumericAcross = (row) => {
      if (!row) return "";
      for (const k of ["Wind", "Solar", "RT", "Hybrid"]) {
        const v = row[k];
        const n = coerceNum(v);
        if (n !== "") return n;
      }
      return "";
    };

    // Helper to render the maintenance sub-table to mirror the operational cost config structure, filling from any numeric column
    const renderMaintenanceSubTable = (blockRows) => {
      // Extract values by label for convenience
      const rowMap = {};
      blockRows.forEach((r) => {
        rowMap[(r[cols[0]] || "").trim()] = r;
      });
      const val = (label) => firstNumericAcross(rowMap[label]);
      const gp = globalParams || {};

      const solarCharge = coerceNum(gp.annual_maintenance_per_solar_unit_in_lakhs) || val("Solar - Annual Maintenance per MWp");
      const solarEsc = coerceNum(gp.solar_escalation_perc) || val("Solar - Escalation for O&M - pa");

      const windParsed = parseSeries(gp.annual_maintenance_per_turbine_in_lakhs);
      const windCharge = (windParsed.scalar !== "" ? windParsed.scalar : "") || val("Wind - Annual Maintenance per Turbine");
      const windBopCharge = coerceNum(gp.annual_maintenance_per_turbine_in_lakhs_with_bop) || val("Wind - Annual Maintenance per Turbine (BOP)");
      const windEsc = coerceNum(gp.wind_escalation_perc) || val("Wind - Escalation for O&M - pa");
      const windBopEsc = coerceNum(gp.wind_bop_escalation_perc) || val("Wind - BOP Escalation - pa");
      const freeOandMPeriod = coerceNum(gp.no_of_bop_year) || val("Free O&amp;M period in Years");

      const hasSeries = Array.isArray(windParsed.series) && windParsed.series.length > 1;

      const windChargeDisplay = hasSeries ? "See below" : windCharge;
      const windEscDisplay = hasSeries ? "See below" : windEsc;

      return (
        <tr className="maintenance-subtable-row">
          <td colSpan={cols.length}>
            <div className="summary-table-wrapper">
              <table className="summary-table maintenance-table">
                <thead>
                  <tr>
                    <th>Particular</th>
                    <th>O &amp; M charges in lakh</th>
                    <th>Annual Escalation in %</th>
                    <th>Free O&amp;M period in Years</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Solar</td>
                    <td className="numeric-cell">{solarCharge}</td>
                    <td className="numeric-cell">{solarEsc}</td>
                    <td className="numeric-cell">0</td>
                  </tr>
                  <tr>
                    <td>Wind</td>
                    <td className="numeric-cell">{windChargeDisplay}</td>
                    <td className="numeric-cell">{windEscDisplay}</td>
                    <td className="numeric-cell">{freeOandMPeriod}</td>
                  </tr>
                  <tr>
                    <td>Wind BOP</td>
                    <td className="numeric-cell">{windBopCharge}</td>
                    <td className="numeric-cell">{windBopEsc}</td>
                    <td className="numeric-cell">0</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {hasSeries && (
              <div className="summary-table-wrapper" style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 700, margin: '6px 0' }}>Turbine O &amp; M</div>
                <table className="summary-table maintenance-series-table">
                  <tbody>
                    {windParsed.series.map((v, idx) => (
                      <tr key={idx}>
                        <td>{ordinal(idx + 1)} year</td>
                        <td className="numeric-cell">{v}</td>
                      </tr>
                    ))}
                    <tr>
                      <td>Escalation from</td>
                      <td className="numeric-cell">{windEsc !== "" ? `${windEsc}%` : ""}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      );
    };

    const isMaintenanceDetail = (val) => {
      if (!val) return false;
      const v = val.toLowerCase();
      return v.startsWith("solar -") || v.startsWith("wind -");
    };

    const bodyRows = [];
    let maintenanceRendered = false;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const label = row[cols[0]];
      const isSpacer = label === "" || label === null || label === undefined;

      if (isSpacer) {
        bodyRows.push(
          <tr key={`spacer-${i}`} className="spacer-row">
            <td colSpan={cols.length} style={{ background: '#ffffff', height: '10px' }}>&nbsp;</td>
          </tr>
        );
        continue;
      }

      if (isSectionRow(label)) {
        bodyRows.push(
          <tr key={`section-${i}`} className="section-row">
            <td colSpan={cols.length} style={{ fontWeight: 700, background: '#f3f4f6' }}>{label}</td>
          </tr>
        );

        // If this is the maintenance block, render the subtable and skip its detail rows
        if (label === "Annual Maintenance Assumptions (excl GST)") {
          const blockRows = [];
          let j = i + 1;
          for (; j < rows.length; j++) {
            const nextLabel = rows[j][cols[0]] || "";
            if (isSectionRow(nextLabel) || nextLabel === "" || nextLabel === null || nextLabel === undefined) break;
            blockRows.push(rows[j]);
          }
          bodyRows.push(renderMaintenanceSubTable(blockRows));
          maintenanceRendered = true;
          i = j - 1; // advance outer loop
        }
        continue;
      }

      // Skip maintenance detail rows if we've already rendered the maintenance block
      if (maintenanceRendered && isMaintenanceDetail(label)) {
        continue;
      }

      bodyRows.push(
        <tr key={i}>
          {cols.map((c, cIdx) => {
            const isTotalCol = String(c || '').trim().toLowerCase() === 'hybrid';
            const value = isTotalCol ? getProjectDetailTotalValue(label, row[c]) : row[c];
            return (
              <td key={cIdx} className={cIdx === 0 ? '' : 'numeric-cell'}>
                {value}
              </td>
            );
          })}
        </tr>
      );
    }

    return (
      <div className="summary-table-wrapper">
        <table className="summary-table assumptions-table">
          <thead>
            <tr>
              {cols.map((c, idx) => (
                <th key={idx}>{idx === 0 ? 'Particulars' : (String(c || '').trim().toLowerCase() === 'hybrid' ? 'Total' : c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows}
          </tbody>
        </table>
      </div>
    );
  };

  // Embed logo for print window (blob URLs can't reliably load relative assets)
  const getIntegrumLogoDataUrl = async () => {
    const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const logoUrl = `${publicBase}/integrum-energy-logo.png`;
    const res = await fetch(logoUrl);
    if (!res.ok) throw new Error(`Failed to load logo: ${res.status} ${res.statusText}`);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read logo blob'));
      reader.readAsDataURL(blob);
    });
  };

  const makeSummaryPdfFilename = () => {
    const rawName = session.pipeline_name || 'pipeline';
    const safeName = String(rawName).replace(/[^a-z0-9._-]+/gi, '_').replace(/^_+|_+$/g, '') || 'pipeline';
    return `Project Summary - ${safeName}.pdf`;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const printSummaryHtmlFallback = (html) => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    const cleanup = () => {
      URL.revokeObjectURL(url);
      if (printWindow && !printWindow.closed) {
        printWindow.close();
      }
    };

    if (!printWindow) {
      cleanup();
      throw new Error('Popup blocked. Allow popups to download PDF.');
    }

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(cleanup, 500);
    };
  };

  // Assumptions HTML builder for PDF export (shared between summary + assumptions-only exports)
  const buildAssumptionsExportSectionsHtml = (options = {}) => {
    if (!projectAssumptions?.rows?.length) return `<div class="meta">No project assumptions data available.</div>`;
    const includeTitleSet = Array.isArray(options.includeTitles)
      ? new Set(options.includeTitles.map((title) => String(title || '').trim().toLowerCase()))
      : null;

    const cols = projectAssumptions.columns || [];
    const rows = projectAssumptions.rows || [];

    const sectionNames = new Set([
      "Project Details",
      "Funding Plan",
      "Loan Assumptions",
      "Other Assumptions",
      "Lease assumptions",
      "Annual Maintenance Assumptions (excl GST)",
      "Battery Summary",
      "Funding Plan - Equity 100%",
      "Debt - 75.0%",
      "Debt - 75%",
    ].map(s => s.toLowerCase()));

    const isSectionRow = (val) => {
      const v = (val || "").trim();
      if (!v) return false;
      return sectionNames.has(v.toLowerCase());
    };

    const isMaintenanceDetail = (val) => {
      if (!val) return false;
      const v = val.toLowerCase();
      return v.startsWith("solar -") || v.startsWith("wind -");
    };

    const firstNumericAcross = (row) => {
      if (!row) return "";
      for (const k of ["Wind", "Solar", "RT", "Hybrid"]) {
        const v = row[k];
        if (v === null || v === undefined || v === "") continue;
        const n = Number(v);
        if (isFinite(n)) return n;
      }
      return "";
    };

    const buildMaintenanceSubTableHtml = (blockRows) => {
      const rowMap = {};
      blockRows.forEach((r) => {
        rowMap[(r[cols[0]] || "").trim()] = r;
      });
      const gp = globalParams || {};
      const val = (label) => firstNumericAcross(rowMap[label]);

      const solarCharge = coerceNum(gp.annual_maintenance_per_solar_unit_in_lakhs) || val("Solar - Annual Maintenance per MWp");
      const solarEsc = coerceNum(gp.solar_escalation_perc) || val("Solar - Escalation for O&M - pa");

      const windParsed = parseSeries(gp.annual_maintenance_per_turbine_in_lakhs);
      const windCharge = (windParsed.scalar !== "" ? windParsed.scalar : "") || val("Wind - Annual Maintenance per Turbine");
      const windBopCharge = coerceNum(gp.annual_maintenance_per_turbine_in_lakhs_with_bop) || val("Wind - Annual Maintenance per Turbine (BOP)");
      const windEsc = coerceNum(gp.wind_escalation_perc) || val("Wind - Escalation for O&M - pa");
      const windBopEsc = coerceNum(gp.wind_bop_escalation_perc) || val("Wind - BOP Escalation - pa");
      const freeOandMPeriod = coerceNum(gp.no_of_bop_year) || val("Free O&M period in Years");
      const hasSeries = Array.isArray(windParsed.series) && windParsed.series.length > 1;
      const windChargeDisplay = hasSeries ? "See below" : windCharge;
      const windEscDisplay = hasSeries ? "See below" : windEsc;

      return `
        <div class="table-wrapper">
          <table class="maintenance-table">
            <thead>
              <tr>
                <th>Particular</th>
                <th>O &amp; M charges in lakh</th>
                <th>Annual Escalation in %</th>
                <th>Free O&amp;M period in Years</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Solar</td>
                <td class="number">${solarCharge}</td>
                <td class="number">${solarEsc}</td>
                <td class="number">0</td>
              </tr>
              <tr>
                <td>Wind</td>
                <td class="number">${windChargeDisplay}</td>
                <td class="number">${windEscDisplay}</td>
                <td class="number">${freeOandMPeriod}</td>
              </tr>
              <tr>
                <td>Wind BOP</td>
                <td class="number">${windBopCharge}</td>
                <td class="number">${windBopEsc}</td>
                <td class="number">0</td>
              </tr>
            </tbody>
          </table>
        </div>
        ${hasSeries ? `
        <div class="table-wrapper" style="margin-top:8px;">
          <div style="font-weight:700;margin:4px 0;">Turbine O &amp; M</div>
          <table class="compact-table">
            <thead>
              <tr>
                <th class="particular">Year</th>
                <th class="value">O &amp; M (lakh)</th>
              </tr>
            </thead>
            <tbody>
              ${windParsed.series.map((v, idx) => `
                <tr>
                  <td class="particular">${ordinal(idx + 1)} year</td>
                  <td class="value">${v}</td>
                </tr>
              `).join('')}
              <tr>
                <td class="particular">Escalation from</td>
                <td class="value">${windEsc !== "" ? `${windEsc}%` : ""}</td>
              </tr>
            </tbody>
          </table>
        </div>
        ` : ``}`;
    };

    // Split into per-section blocks.
    const sections = [];
    let current = null;
    let maintenanceRendered = false;
    const pushCurrent = () => {
      if (current) sections.push(current);
      current = null;
    };

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const label = row[cols[0]];
      const isSpacer = label === "" || label === null || label === undefined;

      if (isSectionRow(label)) {
        pushCurrent();
        current = { title: label, items: [] };
        if ((label || "").trim().toLowerCase() === "annual maintenance assumptions (excl gst)".toLowerCase()) {
          const blockRows = [];
          let j = i + 1;
          for (; j < rows.length; j++) {
            const nextLabel = rows[j][cols[0]] || "";
            if (isSectionRow(nextLabel) || nextLabel === "" || nextLabel === null || nextLabel === undefined) break;
            blockRows.push(rows[j]);
          }
          current.items.push({ type: "html", html: buildMaintenanceSubTableHtml(blockRows) });
          maintenanceRendered = true;
          i = j - 1;
        }
        continue;
      }

      if (!current) current = { title: "Project Assumptions", items: [] };
      if (maintenanceRendered && isMaintenanceDetail(label)) continue;
      if (isSpacer) {
        current.items.push({ type: "spacer" });
        continue;
      }
      current.items.push({ type: "row", row });
    }
    pushCurrent();

    const norm = (v) => (v === null || v === undefined ? "" : String(v));
    const isEmpty = (v) => norm(v).trim() === "";
    const formatCompactValue = (r) => {
      const values = [];
      ["Wind", "Solar", "RT", "Hybrid"].forEach((k) => {
        const v = r?.[k];
        if (!isEmpty(v)) values.push({ k, v: norm(v) });
      });
      if (values.length === 0) return "";
      if (values.length === 1) return values[0].v;
      const allSame = values.every((x) => x.v === values[0].v);
      if (allSame) return values[0].v;
      return values.map((x) => `${x.k}: ${x.v}`).join(", ");
    };
    const valueCellClass = (val) => {
      const v = norm(val).trim();
      if (!v) return "value";
      const numericLike = /^-?\d+(?:\.\d+)?%?$/.test(v);
      return numericLike ? "value" : "value text";
    };
    const assumptionHeaderLabel = (label) => {
      const text = norm(label).trim();
      return text.toLowerCase() === 'hybrid' ? 'Total' : text;
    };
    const coerceGlobalNumber = (val, fallback = 0) => {
      if (val === null || val === undefined || val === '') return fallback;
      if (typeof val === 'number') return isFinite(val) ? val : fallback;
      if (typeof val === 'string') {
        const n = parseFloat(val);
        return isFinite(n) ? n : fallback;
      }
      if (typeof val === 'object') {
        if (val.__default__ !== null && val.__default__ !== undefined && val.__default__ !== '') {
          const n = parseFloat(val.__default__);
          if (isFinite(n)) return n;
        }
        if (val.__by_node__ && typeof val.__by_node__ === 'object') {
          const first = Object.values(val.__by_node__)[0];
          const n = parseFloat(first);
          if (isFinite(n)) return n;
        }
      }
      return fallback;
    };
    const coerceBool = (val) => {
      if (typeof val === 'boolean') return val;
      if (typeof val === 'number') return val !== 0;
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        return ['true', '1', 'yes', 'y'].includes(v);
      }
      return false;
    };
    const getCapperEvacuationLimitMw = () => {
      const capperNode = (session.nodes || []).find((n) => n.node_type === 'capper');
      if (!capperNode) return NaN;
      const params = capperNode?.node_params?.[0] || {};
      const manualCapLimit = coerceGlobalNumber(params.cap_limit, NaN);
      if (isFinite(manualCapLimit)) return manualCapLimit / 1000;

      const gp = globalParams || {};
      const windTurbines = coerceGlobalNumber(gp.mult_factor_wind ?? gp.no_of_wind_turbines, 0);
      const solarMw = coerceGlobalNumber(gp.mult_factor_solar ?? gp.no_of_solar_units, 0);
      const mwPerTurbine = coerceGlobalNumber(gp.mw_per_turbine, 2.1) || 2.1;
      const ratioRaw = coerceGlobalNumber(params.acdc_ratio ?? params.capping_limit, 1);
      const acdcRatio = ratioRaw === 0 ? 1 : ratioRaw;
      const loss = coerceGlobalNumber(params.gen_to_trans_loss, 0);
      const windMw = Math.round((windTurbines * mwPerTurbine) * 100) / 100;
      const effectiveSolarMw = coerceBool(gp.Btm_flag) ? 0 : Math.round(solarMw * 100) / 100;
      const lossFactor = 1 + loss;

      if (coerceBool(params.KA)) {
        return Math.max(windMw, effectiveSolarMw / acdcRatio) * lossFactor;
      }
      return (windMw + (effectiveSolarMw / acdcRatio)) * lossFactor;
    };
    const getProjectDetailHybridValue = (label, hybrid) => {
      const key = norm(label).trim().toLowerCase();
      if (key === 'no of turbines') return '-';
      if (key === 'mwps') {
        const evacuationLimitMw = getCapperEvacuationLimitMw();
        if (isFinite(evacuationLimitMw)) return format2(evacuationLimitMw);
      }
      return hybrid;
    };

    let out = '';
    let compactMode = Boolean(options.forceCompact);

    // Render (Loan + Lease) and (Other + Annual Maintenance) side-by-side (to conserve space).
    const titleKey = (t) => norm(t).trim().toLowerCase();
    const LOAN_TITLE = "loan assumptions";
    const LEASE_TITLE = "lease assumptions";
    const OTHER_TITLE = "other assumptions";
    const MAINT_TITLE = "annual maintenance assumptions (excl gst)";
    const loanIdx = sections.findIndex((s) => titleKey(s?.title) === LOAN_TITLE);
    const leaseIdx = sections.findIndex((s) => titleKey(s?.title) === LEASE_TITLE);
    const leaseSection = leaseIdx >= 0 ? sections[leaseIdx] : null;
    const otherIdx = sections.findIndex((s) => titleKey(s?.title) === OTHER_TITLE);
    const maintIdx = sections.findIndex((s) => titleKey(s?.title) === MAINT_TITLE);
    const maintSection = maintIdx >= 0 ? sections[maintIdx] : null;

    const sectionsToRender = sections.filter((section, idx) => {
      if (includeTitleSet && !includeTitleSet.has(titleKey(section?.title))) return false;
      if (loanIdx >= 0 && leaseSection && idx === leaseIdx) return false;
      if (otherIdx >= 0 && maintSection && idx === maintIdx) return false;
      return true;
    });

    const renderSectionHtml = (sec, useCompact) => {
      const sectionKey = titleKey(sec?.title);
      if (sectionKey === 'battery summary') {
        const bodyRows = (sec.items || []).map((it) => {
          if (it.type === "spacer") return "";
          if (it.type !== "row") return "";
          const r = it.row || {};
          const label = norm(r[cols[0]]);
          const val = formatCompactValue(r);
          return `
            <tr>
              <td class="particular">${label}</td>
              <td class="${valueCellClass(val)}">${norm(val)}</td>
            </tr>`;
        }).join("");

        return `
  <div class="assump-section battery-summary-export">
    <h2>${sec.title}</h2>
    <div class="table-wrapper">
      <table class="compact-table">
        <tbody>
          ${bodyRows}
        </tbody>
      </table>
    </div>
  </div>`;
      }

      // Treat "html-only" sections as those whose non-spacer items are only a single HTML block.
      // This avoids rendering an outer "Particulars / Value" header when the section content is itself a full table
      // (notably: Annual Maintenance Assumptions).
      const nonSpacerItems = Array.isArray(sec.items) ? sec.items.filter((it) => it && it.type !== "spacer") : [];
      const htmlOnly = nonSpacerItems.length === 1 && nonSpacerItems[0]?.type === "html";
      if (htmlOnly) {
        return `
  <div class="assump-section">
    <h2>${sec.title}</h2>
    ${nonSpacerItems[0].html}
  </div>`;
      }

      if (!useCompact) {
        const bodyRows = (sec.items || []).map((it) => {
          if (it.type === "spacer") return `<tr class="spacer-row"><td colspan="${cols.length}" style="background:#ffffff;height:6px;">&nbsp;</td></tr>`;
          if (it.type === "html") return it.html;
          if (it.type !== "row") return "";
          const r = it.row || {};
          const label = norm(r[cols[0]]);
          const cells = cols.slice(1).map((colName) => {
            const value = String(colName || '').trim().toLowerCase() === 'hybrid'
              ? getProjectDetailHybridValue(label, norm(r[colName]))
              : norm(r[colName]);
            return `<td class="number">${value}</td>`;
          }).join("");
          return `
            <tr>
              <td>${label}</td>
              ${cells}
            </tr>`;
        }).join("");
        const headerCells = cols.slice(1).map((colName) => (
          `<th>${assumptionHeaderLabel(colName)}</th>`
        )).join("");

        return `
  <div class="assump-section">
    <h2>${sec.title}</h2>
    <div class="table-wrapper">
      <table class="assumption-wide-table">
        <thead>
          <tr>
            <th style="width:36%;">Particulars</th>
            ${headerCells}
          </tr>
        </thead>
        <tbody>
          ${bodyRows}
        </tbody>
      </table>
    </div>
  </div>`;
      }

      const bodyRows = (sec.items || []).map((it) => {
        if (it.type === "spacer") return "";
        if (it.type === "html") return `<tr><td colspan="2">${it.html}</td></tr>`;
        if (it.type !== "row") return "";
        const r = it.row || {};
        const label = norm(r[cols[0]]);
        const val = formatCompactValue(r);
        return `
            <tr>
              <td class="particular">${label}</td>
              <td class="${valueCellClass(val)}">${norm(val)}</td>
            </tr>`;
      }).join("");

      return `
  <div class="assump-section">
    <h2>${sec.title}</h2>
    <div class="table-wrapper">
      <table class="compact-table">
        <thead>
          <tr>
            <th class="particular">Particulars</th>
            <th class="value">Value</th>
          </tr>
        </thead>
        <tbody>
          ${bodyRows}
        </tbody>
      </table>
    </div>
  </div>`;
    };

    sectionsToRender.forEach((sec) => {
      const key = titleKey(sec?.title);
      if (key === LOAN_TITLE) compactMode = true;

      // When we hit "Loan Assumptions", render "Lease assumptions" next to it.
      if (key === LOAN_TITLE && leaseSection) {
        out += `
  <div class="assump-two-col">
    ${renderSectionHtml(sec, true)}
    ${renderSectionHtml(leaseSection, true)}
  </div>`;
        return;
      }

      // When we hit "Other Assumptions", render "Annual Maintenance Assumptions" next to it.
      if (key === OTHER_TITLE && maintSection) {
        out += `
  <div class="assump-two-col">
    ${renderSectionHtml(sec, compactMode)}
    ${renderSectionHtml(maintSection, compactMode)}
  </div>`;
        return;
      }

      out += renderSectionHtml(sec, compactMode);
    });

    return out;
  };

  // HTML Download Handler (prints to PDF-friendly window)
  const handleDownloadSummaryPdf = async () => {
    try {
      const logoDataUrl = await getIntegrumLogoDataUrl();
      // Helper: decide if a finance metric should be shown
      const shouldIncludeFinanceMetric = (name) => {
        if (!name) return false;
        const key = String(name).toLowerCase();
        const blockedFinance = [
          'avg_annual_savings',
          'avg_savings_during_debt',
          'avg_roe (%)',
          'avg_roi (%)',
          'equity_irr',
          'project_irr (%)',
          'pay_back_period'
        ];
        return !blockedFinance.includes(key);
      };

      // Helper: decide if an op-cost metric should be shown
      const shouldIncludeOpMetric = (name) => {
        if (!name) return false;
        const key = String(name).toLowerCase();
        if (key.includes('consumer effective levellised cost')) return false;
        if (key.includes('total lapsed units for excess revenue')) return false;
        if (key.includes('revenue from excess banked units')) return false;
        return true;
      };

      // Transpose helper for export: metric names on the left, years across the top.
      
      const buildYearsTopTable = (rows, metricNameCol = 'Year') => {
        if (!rows || !rows.length) return null;
        // Derive year columns from ALL rows (not just the first), so we never miss columns like "25".
        const yearSet = new Set();
        rows.forEach((r) => {
          Object.keys(r || {}).forEach((c) => {
            if (c === metricNameCol) return;
            const key = String(c).trim();
            if (/^\d+$/.test(key)) yearSet.add(key);
          });
        });
        const yearColumns = Array.from(yearSet).sort((a, b) => Number(a) - Number(b));

        let headerHtml = `
          <tr>
            <th class="row-header">Particular</th>`;
        yearColumns.forEach((y) => {
          // Keep headers compact (no "Year" prefix) to maximize usable width in PDF export.
          headerHtml += `<th>${y}</th>`;
        });
        headerHtml += `
          </tr>`;

        let bodyHtml = '';
        rows.forEach((r) => {
          const metric = r?.[metricNameCol];
          if (!metric) return;
          bodyHtml += `
          <tr>
            <td class="row-header">${metric}</td>`;
          yearColumns.forEach((y) => {
            const val = r?.[y];
            const n = Number(val);
            const formatted = isFinite(n) ? format1(n) : (val ?? '');
            bodyHtml += `<td class="number">${formatted}</td>`;
          });
          bodyHtml += `
          </tr>`;
        });

        return { headerHtml, bodyHtml, yearColumnsCount: yearColumns.length, rowCount: rows.length };
      };

      const pipelineTitle = session.pipeline_name || 'Pipeline';
      const generatedAt = new Date();

      // Build HTML content
      let html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Project Summary - ${pipelineTitle}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    :root{
      --brand-ink:#0b3a5a;
      --brand-gold:#f2b705;
      --text:#111827;
      --muted:#6b7280;
      --border:#e5e7eb;
      --bg:#f9fafb;
      --panel:#ffffff;
    }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.35;
      color: var(--text);
      padding: 0;
      background: var(--bg);
    }
    /* Footer is fixed; reserve space so content doesn't overlap it */
    :root{
      --footer-offset: 14mm;
    }
    .page {
      padding: 10mm 10mm var(--footer-offset) 10mm;
    }
    .pdf-page {
      padding: 6mm 7mm var(--footer-offset) 7mm;
      break-after: page;
      page-break-after: always;
    }
    .pdf-page .container {
      padding: 10px 10px 6px 10px;
    }
    .pdf-page .report-header {
      padding: 5mm 7mm 4mm 7mm;
      margin-bottom: 5mm;
    }
    .assumption-core-page .report-header {
      padding: 3mm 6mm 2.5mm 6mm;
      margin-bottom: 3mm;
    }
    .assumption-core-page .report-title {
      font-size: 16px;
    }
    .assumption-core-page .report-subtitle {
      font-size: 10px;
      margin-top: 1px;
    }
    .assumption-core-page .brand-logo {
      width: 125px;
    }
    .pdf-page:last-of-type {
      break-after: auto;
      page-break-after: auto;
    }
    .container { 
      width: 100%;
      max-width: 100%;
      margin: 0 auto; 
      background: var(--panel);
      padding: 16px 16px 10px 16px;
      border: 1px solid var(--border);
      border-radius: 10px;
    }
    .report-header{
      position: relative;
      background: #fff;
      border-bottom: 2px solid var(--brand-ink);
      padding: 10mm 10mm 6mm 10mm;
      margin-bottom: 10mm;
    }
    .report-header-inner{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 16px;
    }
    .brand-block{
      display:flex;
      flex-direction:column;
      gap: 3px;
      min-width: 0;
    }
    .report-title{
      font-size: 16px;
      line-height: 1.15;
      font-weight: 800;
      letter-spacing: 0.2px;
      color: var(--brand-ink);
      margin: 0;
    }
    .report-subtitle{
      font-size: 10px;
      color: var(--muted);
      margin-top: 2px;
    }
    .brand-logo{
      width: 155px;
      height: auto;
      object-fit: contain;
    }
    .report-footer{
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: #fff;
      border-top: 1px solid var(--border);
      padding: 4mm 10mm;
      font-size: 10px;
      color: var(--muted);
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 10px;
    }
    .meta { 
      color: var(--muted); 
      margin-bottom: 8px;
      font-size: 10px;
    }
    h2 { 
      font-size: 14px; 
      margin: 5px 0 5px 0;
      color: var(--brand-ink);
      padding: 4px 7px;
      background: #f3f4f6;
      border-left: 4px solid var(--brand-gold);
      font-weight: 800;
      line-height: 1.2;
      break-after: avoid;
      page-break-after: avoid;
    }
    .table-wrapper {
      width: 100%;
      overflow-x: visible;
      margin-bottom: 16px;
      border: 1px solid var(--border);
      border-radius: 8px;
    }
    /* Use this class when a single table must fit on one PDF page.
       We do NOT try to "compact" the table; instead we let it size naturally and scale-to-fit width if needed. */
    .fit-one-page th, .fit-one-page td { padding: 6px 8px; line-height: 1.35; }
    /* Auto layout allows wider year columns; if total width exceeds the page we scale down uniformly. */
    .fit-one-page table { table-layout: auto; }
    /* Keep the "Particular" column readable and prevent it collapsing (Savings/IRR issue). */
    .fit-one-page th.row-header,
    .fit-one-page td.row-header {
      width: 260px;
      min-width: 260px;
      white-space: normal;
      word-break: normal;
      overflow-wrap: anywhere;
    }
    /* Years + numeric cells should not wrap; wrapping forces cramped headers and makes the table look compact. */
    .fit-one-page th:not(.row-header),
    .fit-one-page td.number { white-space: nowrap; }
    .fit-one-page { break-inside: avoid; page-break-inside: avoid; }
    .fit-scale {
      transform: scale(var(--fit-scale, 1));
      transform-origin: top left;
      width: calc(100% / var(--fit-scale, 1));
      break-inside: avoid;
      page-break-inside: avoid;
    }

    table { 
      width: 100%; 
      border-collapse: collapse; 
      font-size: 12px;
      background: #fff;
      table-layout: fixed;
    }
    th, td { 
      padding: 8px 10px; 
      text-align: left; 
      border: 1px solid var(--border);
      white-space: normal;
      word-break: break-word;
      line-height: 1.35;
    }
    th { 
      background: var(--brand-ink);
      color: white;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 11px;
      letter-spacing: 0.02em;
      line-height: 1.3;
      white-space: normal;
      word-break: break-word;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    th.row-header {
      position: sticky;
      left: 0;
      z-index: 20;
      background: var(--brand-ink);
    }
    td.row-header {
      position: sticky;
      left: 0;
      background: #f3f4f6;
      font-weight: 600;
      z-index: 5;
      border-right: 2px solid #9ca3af;
    }
    tr:nth-child(even) td:not(.row-header) { background: #f9fafb; }
    tr:hover td:not(.row-header) { background: #f3f4f6; }
    td.number { 
      text-align: right; 
      font-variant-numeric: tabular-nums;
      font-weight: 500;
    }
    td.negative { color: #dc2626; }
    tr.total td { 
      font-weight: 700; 
      background: #e5e7eb;
      border-top: 2px solid #9ca3af;
    }
    tr.total td.row-header {
      background: #d1d5db;
    }
    .page-break { 
      page-break-after: always; 
      margin: 16px 0;
      border-bottom: 2px dashed #d1d5db;
    }
    .section {
      page-break-inside: avoid;
      break-inside: avoid;
      margin-bottom: 12px;
    }
    /* Force a new page before critical wide tables so they never split */
    .page-start {
      break-before: page;
      page-break-before: always;
    }
    .grid-container,
    .metric-card,
    .table-wrapper,
    table,
    thead,
    tbody,
    tfoot,
    tr,
    td,
    th {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    @media print {
      /* Reclaim width so wide year tables scale less (looks less "compacted") */
      @page { size: A4 landscape; margin: 6mm; }
      body {
        padding: 0;
        background: white;
        /* Ensure zebra shading/background colors are preserved in PDF output */
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        color-adjust: exact;
      }
      .page { padding: 0 0 var(--footer-offset) 0; }
      .pdf-page {
        padding: 0 0 var(--footer-offset) 0;
        break-after: page;
        page-break-after: always;
      }
      .pdf-page:last-of-type {
        break-after: auto;
        page-break-after: auto;
      }
      .report-header{ margin-bottom: 6mm; }
      .container { box-shadow: none; }
      .pdf-page .container { padding: 8px; }
      .pdf-page .report-header { padding: 4mm 6mm 3mm 6mm; margin-bottom: 4mm; }
      .assumption-core-page .report-header { padding: 2.5mm 5mm 2mm 5mm; margin-bottom: 2.5mm; }
      .section {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      /* Start each yearwise dataframe on a fresh page so it never "spills" from previous content */
      .page-start { break-before: page; page-break-before: always; }
      .page-break { page-break-after: always; border: none; }
      tr:hover { background: inherit !important; }
      /* Keep the SAME font size/padding as our base table; scaling handles fit-to-page. */
      table { font-size: 12px; }
      th, td { padding: 8px 10px; }
      .fit-one-page table { font-size: 12px; }
      .fit-one-page th, .fit-one-page td { padding: 8px 10px; }
      /* Reduce chrome so tables can be larger */
      .container { padding: 10px; border-radius: 0; }
      .table-wrapper { border: 0; }
      /* Sticky headers/columns can overlap in print; disable for PDF rendering */
      th, th.row-header, td.row-header { position: static !important; }

      /* Use zoom for fit-to-page in print so pagination uses the scaled layout box (transform alone doesn't). */
      .fit-scale {
        transform: none !important;
        zoom: var(--fit-scale, 1);
      }

      /* Yearwise tables: fit all 25 year columns inside the printable page width. */
      .table-wrapper.fit-one-page.years-top table {
        font-size: 11px !important;
        table-layout: fixed !important;
        width: 100% !important;
        min-width: 0 !important;
      }
      .table-wrapper.fit-one-page.years-top th,
      .table-wrapper.fit-one-page.years-top td {
        padding: 4px 1px !important;
        font-size: 11px !important;
        line-height: 1.2 !important;
        font-family: "Arial Narrow", Arial, Helvetica, sans-serif !important;
        font-weight: 700 !important;
      }
      .table-wrapper.fit-one-page.years-top th.row-header,
      .table-wrapper.fit-one-page.years-top td.row-header {
        width: 16% !important;
        min-width: 0 !important;
        max-width: none !important;
        padding: 4px 5px !important;
      }
      .table-wrapper.fit-one-page.years-top th:not(.row-header),
      .table-wrapper.fit-one-page.years-top td:not(.row-header) {
        width: auto !important;
        min-width: 0 !important;
        max-width: none !important;
        padding: 4px 1px !important;
        overflow: hidden !important;
      }

      /* Alternate row shading (print) for readability */
      .table-wrapper.fit-one-page.years-top table,
      .table-wrapper.fit-one-page.years-top thead,
      .table-wrapper.fit-one-page.years-top tbody,
      .table-wrapper.fit-one-page.years-top tr,
      .table-wrapper.fit-one-page.years-top td,
      .table-wrapper.fit-one-page.years-top th {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        color-adjust: exact;
      }
      .table-wrapper.fit-one-page.years-top tbody tr:nth-child(odd) td {
        background: #ffffff !important;
      }
      .table-wrapper.fit-one-page.years-top tbody tr:nth-child(even) td {
        background: #f8fafc !important;
      }
      .table-wrapper.fit-one-page.years-top tbody tr:nth-child(odd) td.row-header {
        background: #f3f4f6 !important;
      }
      .table-wrapper.fit-one-page.years-top tbody tr:nth-child(even) td.row-header {
        background: #e9eef5 !important;
      }
    }
    .two-column-table { display: inline-block; width: 48%; vertical-align: top; }
    .grid-container { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 14px; }
    .metric-card { 
      padding: 8px 10px; 
      background: #f9fafb; 
      border: 1px solid #e5e7eb; 
      border-radius: 6px;
    }
    .metric-label { 
      font-size: 12px; 
      color: #111827; 
      margin-bottom: 4px;
      font-weight: 800;
      line-height: 1.2;
    }
    .metric-value { 
      font-size: 18px; 
      font-weight: 800; 
      color: #111827;
      font-variant-numeric: tabular-nums;
    }
    .settlement-table {
      table-layout: fixed;
      font-size: 11px;
    }
    .settlement-table th {
      font-size: 10px;
      font-weight: 800;
      text-align: center;
      padding: 5px 6px;
      line-height: 1.18;
    }
    .settlement-table td {
      font-size: 11px;
      font-weight: 650;
      text-align: center;
      padding: 5px 6px;
      line-height: 1.18;
    }
    .settlement-table td.number {
      text-align: center;
      font-weight: 800;
    }
    .settlement-table tr.total td {
      font-weight: 900;
    }

    /* Yearwise (Pages 7 & 8): fit all 25 year columns with readable values */
    .table-wrapper.fit-one-page.years-top table {
      font-size: 11px !important;
      table-layout: fixed !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
    }
    .table-wrapper.fit-one-page.years-top th,
    .table-wrapper.fit-one-page.years-top td {
      padding: 5px 1px !important;
      font-size: 11px !important;
      text-align: center !important;
      border: 1px solid #d1d5db !important;
      line-height: 1.2 !important;
      font-family: "Arial Narrow", Arial, Helvetica, sans-serif !important;
      font-weight: 700 !important;
    }
    .table-wrapper.fit-one-page.years-top th.row-header,
    .table-wrapper.fit-one-page.years-top td.row-header {
      width: 16% !important;
      min-width: 0 !important;
      max-width: none !important;
      white-space: normal !important;
      word-break: break-word !important;
      overflow-wrap: anywhere !important;
      text-align: left !important;
      padding: 5px 5px !important;
    }
    .table-wrapper.fit-one-page.years-top th:not(.row-header),
    .table-wrapper.fit-one-page.years-top td:not(.row-header) {
      width: auto !important;
      min-width: 0 !important;
      max-width: none !important;
      white-space: nowrap !important;
      padding: 5px 1px !important;
      overflow: hidden !important;
    }
    .table-wrapper.fit-one-page.years-top td.number {
      text-align: right !important;
      font-variant-numeric: tabular-nums !important;
      font-family: "Arial Narrow", Arial, Helvetica, sans-serif !important;
      font-weight: 750 !important;
      padding-right: 2px !important;
    }

    /* Alternate row shading for readability (applies to Capex + Savings/IRR yearwise tables) */
    .table-wrapper.fit-one-page.years-top tbody tr:nth-child(odd) td {
      background: #ffffff !important;
    }
    .table-wrapper.fit-one-page.years-top tbody tr:nth-child(even) td {
      background: #f8fafc !important;
    }
    /* Keep the "Particular" column slightly more anchored while still alternating */
    .table-wrapper.fit-one-page.years-top tbody tr:nth-child(odd) td.row-header {
      background: #f3f4f6 !important;
    }
    .table-wrapper.fit-one-page.years-top tbody tr:nth-child(even) td.row-header {
      background: #e9eef5 !important;
    }

    /* Billing Analytics (PDF export) */
    .billing-note{
      color: var(--muted);
      font-size: 10px;
      margin-top: 6px;
      margin-bottom: 8px;
    }
    .billing-grid{
      display:grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-top: 10px;
    }

    /* Billing Analytics: optimized compact table layout */
    .table-wrapper.fit-one-page.billing-analytics-fit table{
      table-layout: fixed !important;
      border-collapse: collapse;
      width: 100% !important;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit table.ba-main{
      font-size: 11px !important;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit table.ba-ops{
      font-size: 11px !important;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit th{
      white-space: nowrap !important;
      background: #1e293b !important;
      color: #fff !important;
      font-weight: 600 !important;
      text-transform: uppercase !important;
      letter-spacing: 0.3px !important;
      font-size: 10px !important;
      padding: 6px 4px !important;
      border: none !important;
      border-bottom: 2px solid #475569 !important;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit td{
      padding: 5px 4px !important;
      border: none !important;
      border-bottom: 1px solid #e2e8f0 !important;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit td.number{
      white-space: nowrap !important;
      text-align: right !important;
      font-variant-numeric: tabular-nums !important;
      font-family: 'SF Mono', Monaco, Consolas, monospace !important;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit td:first-child{
      white-space: nowrap !important;
      font-weight: 500 !important;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit tr.total td{
      background: #f1f5f9 !important;
      border-top: 2px solid #94a3b8 !important;
      border-bottom: 2px solid #94a3b8 !important;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit tr:nth-child(even) td{
      background: #fafbfc;
    }
    .table-wrapper.fit-one-page.billing-analytics-fit tr.total:nth-child(even) td{
      background: #f1f5f9 !important;
    }

    @media print{
      .table-wrapper.fit-one-page.billing-analytics-fit table{ font-size: 11px !important; }
      .table-wrapper.fit-one-page.billing-analytics-fit th{ font-size: 10px !important; padding: 6px 4px !important; }
      .table-wrapper.fit-one-page.billing-analytics-fit td{ padding: 5px 4px !important; }
    }
    .df-highlight {
      font-weight: 700;
    }

    /* ---- Assumptions export (compact tables) ---- */
    .assump-section { break-inside: avoid; page-break-inside: avoid; margin-top: 6px; }
    .assump-section:first-child { margin-top: 0; }
    .assump-section h2 {
      font-size: 23px;
      font-weight: 800;
      letter-spacing: 0;
      margin: 14px 0 10px 0;
      padding: 7px 10px;
    }
    .assump-section .table-wrapper {
      margin-bottom: 12px;
    }
    .assump-section table {
      font-size: 17px;
      color: #111827;
    }
    .assump-section th {
      font-size: 16px;
      font-weight: 800;
      letter-spacing: 0.01em;
      padding: 9px 12px;
    }
    .assump-section td {
      font-size: 17px;
      font-weight: 700;
      padding: 9px 12px;
      line-height: 1.38;
    }
    .assump-section td.number {
      font-weight: 700;
    }
    .assump-section tr.spacer-row td {
      height: 8px !important;
      padding: 2px 0 !important;
    }
    .assumption-core-page .assump-section h2 {
      font-size: 16px;
      margin: 6px 0 5px 0;
      padding: 5px 8px;
    }
    .assumption-core-page .assump-section th {
      font-size: 11px;
      padding: 5px 7px;
    }
    .assumption-core-page .assump-section td {
      font-size: 12px;
      padding: 5px 7px;
      line-height: 1.18;
    }
    .assumption-core-page .assump-section .table-wrapper {
      margin-bottom: 6px;
    }
    .commercial-assumptions-page .assump-section h2 {
      font-size: 14px;
      margin: 5px 0 5px 0;
      padding: 4px 7px;
    }
    .commercial-assumptions-page .assump-section th {
      font-size: 10px;
      padding: 4px 5px;
    }
    .commercial-assumptions-page .assump-section td {
      font-size: 11px;
      padding: 4px 5px;
      line-height: 1.12;
    }
    .commercial-assumptions-page .assump-section .table-wrapper {
      margin-bottom: 5px;
    }
    .commercial-assumptions-page .assump-two-col {
      gap: 10px;
      margin-top: 8px;
    }
    .commercial-assumptions-page .assump-two-col:first-child {
      margin-top: 0;
    }
    .commercial-assumptions-page .maintenance-table th:nth-child(1),
    .commercial-assumptions-page .maintenance-table td:nth-child(1){ width: 31%; }
    .commercial-assumptions-page .maintenance-table th:nth-child(2),
    .commercial-assumptions-page .maintenance-table td:nth-child(2){ width: 27%; }
    .commercial-assumptions-page .maintenance-table th {
      font-size: 9px;
      padding: 4px 4px;
    }
    .commercial-assumptions-page .maintenance-table td {
      font-size: 10px;
      padding: 4px 4px;
    }
    .assumption-wide-table th:not(:first-child),
    .assumption-wide-table td.number {
      text-align: center;
    }
    .assumption-wide-table th:first-child,
    .assumption-wide-table td:first-child {
      text-align: left;
    }
    /* Two-up layout (Loan + Lease) */
    .assump-two-col{
      display: flex;
      gap: 18px;
      align-items: flex-start;
      break-inside: avoid;
      page-break-inside: avoid;
      margin-top: 6px;
    }
    .assump-two-col:first-child{ margin-top: 0; }
    .assump-two-col .assump-section{
      flex: 1 1 0;
      min-width: 0;
      margin-top: 0;
    }
    .maintenance-table{ table-layout: fixed; width: 100%; }
    .maintenance-table th:nth-child(1), .maintenance-table td:nth-child(1){ width: 32%; }
    .maintenance-table th:nth-child(2), .maintenance-table td:nth-child(2){ width: 26%; }
    .maintenance-table th:nth-child(3), .maintenance-table td:nth-child(3){ width: 22%; }
    .maintenance-table th:nth-child(4), .maintenance-table td:nth-child(4){ width: 20%; }
    .compact-table { table-layout: fixed; }
    .compact-table th.particular, .compact-table td.particular { width: 60%; }
    .compact-table th.value, .compact-table td.value { width: 40%; }
    .compact-table td.value { text-align: center; font-variant-numeric: tabular-nums; font-weight: 700; }
    .compact-table td.value.text { text-align: left; }
  </style>
</head>
<body>
  <div class="report-footer">
    <div>Integrum Energy • Internal Use</div>
    <div>Page <span class="pageNumber"></span></div>
  </div>
`;

      const openPdfPage = (withHeader = false, extraClass = '') => `
  <div class="pdf-page ${extraClass}">
    ${withHeader ? `
    <div class="report-header">
      <div class="report-header-inner">
        <div class="brand-block">
          <div class="report-title">Project Summary</div>
          <div class="report-subtitle">Pipeline: ${pipelineTitle}</div>
          <div class="report-subtitle">Generated: ${generatedAt.toLocaleString()}</div>
        </div>
        <img class="brand-logo" src="${logoDataUrl}" alt="Integrum Energy" />
      </div>
    </div>` : ''}
    <div class="container">
`;
      const closePdfPage = () => `
    </div>
  </div>
`;

      const assumptionsCoreTitles = [
        'Project Details',
        'Funding Plan',
        'Battery Summary',
      ];
      const assumptionsCommercialTitles = [
        'Loan Assumptions',
        'Lease assumptions',
        'Other Assumptions',
        'Annual Maintenance Assumptions (excl GST)',
      ];
      const assumptionsAdditionalFundingTitles = [
        'Funding Plan - Equity 100%',
        'Debt - 75.0%',
        'Debt - 75%',
      ];

      // Page 1: Project Assumptions - Core
      html += openPdfPage(true, 'assumption-core-page');
      html += buildAssumptionsExportSectionsHtml({ includeTitles: assumptionsCoreTitles });
      html += closePdfPage();

      // Page 2: Project Assumptions - Loan + Lease + Other + Maintenance
      html += openPdfPage(false, 'commercial-assumptions-page');
      html += buildAssumptionsExportSectionsHtml({ includeTitles: assumptionsCommercialTitles, forceCompact: true });
      html += closePdfPage();

      if (assumptionsAdditionalFundingTitles.some((title) => projectAssumptions?.rows?.some((row) => String(row?.[projectAssumptions?.columns?.[0]] || '').trim().toLowerCase() === title.toLowerCase()))) {
        html += openPdfPage();
        html += buildAssumptionsExportSectionsHtml({ includeTitles: assumptionsAdditionalFundingTitles, forceCompact: true });
        html += closePdfPage();
      }

      // Page 4: Key Financial Summary
      html += openPdfPage();

      // Force Finance Highlights + Consumer Effective Levellised Cost cards onto their OWN page (together),
      // and prevent any other sections from sharing that page.
      let cardsPageHadAny = false;

      // 1) Finance Highlights cards (from financeOutput)
      if (financeOutput && financeOutput.length) {
        const financeCards = getFinanceLastSevenCards();
        if (financeCards.length) {
          const sectionClass = 'section';
          cardsPageHadAny = true;
          html += `
    <div class="${sectionClass}">
      <h2>Finance Highlights</h2>
      <div class="grid-container">`;

          financeCards.forEach(card => {
            html += `
      <div class="metric-card">
        <div class="metric-label">${card.title}</div>
        <div class="metric-value">${card.value}</div>
      </div>`;
          });

          html += `
      </div>
    </div>`;
        }
      }

      // 2) Consumer Effective Levellised Cost cards (from opCostsOutput)
      if (opCostsOutput && opCostsOutput.length) {
        const opCards = getOperationalCards();
        if (opCards.length) {
          const sectionClass = 'section';
          cardsPageHadAny = true;
          html += `
    <div class="${sectionClass}">
      <h2>Consumer Effective Levellised Cost (INR)</h2>
      <div class="grid-container">`;

          opCards.forEach(card => {
            html += `
      <div class="metric-card">
        <div class="metric-label">${card.title}</div>
        <div class="metric-value">${card.value}</div>
      </div>`;
          });

          html += `
      </div>
    </div>`;
        }
      }

      if (!cardsPageHadAny) {
        html += `<div class="meta">No finance or operational cost summary data available.</div>`;
      }
      html += closePdfPage();

      // Page 4: Annual Energy Summary
      html += openPdfPage();
      if (annualMetrics) {
        html += `
    <div class="section">
      <h2>Annual Supply and Consumption Summary</h2>
      <div class="grid-container">`;
        
        const metrics = [
          ['Total annual generation', annualMetrics.totalAnnualGeneration],
          ['Total wind generation', annualMetrics.totalWindGeneration],
          ['Total solar generation', annualMetrics.totalSolarGeneration],
          ['Total curtailed units', annualMetrics.totalCurtailedUnits],
          ['RE supplied units', annualMetrics.reSuppliedUnits],
          ['Total annual consumption', annualMetrics.originalAnnualConsumption],
          ['Balance consumption for proposal', annualMetrics.balanceAnnualConsumption],
          ['Existing OA Solar/Wind', `${formatIndian0(annualMetrics.existingOaReUnits)} (${formatPercent2(annualMetrics.existingOaRePct)})`],
          ['Existing Rooftop Solar (BTM)', `${formatIndian0(annualMetrics.existingBtmUnits)} (${formatPercent2(annualMetrics.existingBtmPct)})`],
          ['Proposed RE replacement', `${formatIndian0(annualMetrics.proposedReUnits)} (${formatPercent2(annualMetrics.proposedRePct)})`],
          ['Overall RE replacement', `${formatIndian0(annualMetrics.overallReUnits)} (${formatPercent2(annualMetrics.overallRePct)})`],
          ['Hourly units banked', annualMetrics.hourlyUnitsBanked],
          // Mirrors whichever Existing OA / Proposed / Total round is selected
          // on screen at export time (bankingCardFigures), same as the live cards.
          ['Lapsed units', bankingCardFigures.lapsedUnits],
          ['Banked units used', bankingCardFigures.bankedUnitsUsed],
          ['Net grid units', bankingCardFigures.netGridUnits],
          ['Units from OA', bankingCardFigures.unitsFromOA],
          ['Battery charging units', annualMetrics.batteryChargingUnits],
          ['Battery discharging units', annualMetrics.batteryDischargingUnits],
        ];

        metrics.forEach(([label, value]) => {
          const displayValue = typeof value === 'string' ? value : formatIndian0(value);
          html += `
      <div class="metric-card">
        <div class="metric-label">${label}</div>
        <div class="metric-value">${displayValue}</div>
      </div>`;
        });

        html += `
      </div>
    </div>`;
      } else {
        html += `<div class="meta">Run relevant nodes (Capper, Scaler, Loss, Consumption, Banking) to see annual energy summary.</div>`;
      }
      html += closePdfPage();

      if (batteryTodTariffsComplete && batteryTodRows.length) {
        const maxBatteryTodCost = Math.max(
          ...batteryTodRows.map((row) => Number(row.gridCostWithoutBattery) || 0),
          1,
        );
        html += openPdfPage();
        html += `
    <div class="section">
      <h2>TOD-wise Battery Grid-cost Impact</h2>
      <div class="grid-container">
        <div class="metric-card"><div class="metric-label">Grid cost without battery</div><div class="metric-value">₹${formatIndian(batteryTodTotals.gridCostWithoutBattery)}</div></div>
        <div class="metric-card"><div class="metric-label">Grid cost with battery</div><div class="metric-value">₹${formatIndian(batteryTodTotals.gridCostWithBattery)}</div></div>
        <div class="metric-card"><div class="metric-label">Battery savings</div><div class="metric-value">₹${formatIndian(batteryTodTotals.savings)}</div></div>
      </div>
      <table class="settlement-table" style="margin-top:16px">
        <thead><tr><th>Time slot</th><th>Grid units</th><th>Battery units</th><th>Tariff</th><th>Without battery</th><th>With battery</th><th>Savings</th></tr></thead>
        <tbody>
          ${batteryTodRows.map((row) => `
            <tr>
              <td>${row.label}</td>
              <td class="number">${formatIndian(row.gridConsumption)}</td>
              <td class="number">${formatIndian(row.batteryDischargeUnits)}</td>
              <td class="number">₹${formatIndian(row.tariff)}</td>
              <td class="number">₹${formatIndian(row.gridCostWithoutBattery)}</td>
              <td class="number">₹${formatIndian(row.gridCostWithBattery)}</td>
              <td class="number">₹${formatIndian(row.savings)}</td>
            </tr>`).join('')}
        </tbody>
      </table>
      <div style="margin-top:18px">
        ${batteryTodRows.map((row) => {
          const withoutWidth = Math.max((row.gridCostWithoutBattery / maxBatteryTodCost) * 100, 0);
          const withWidth = Math.max((row.gridCostWithBattery / maxBatteryTodCost) * 100, 0);
          return `
            <div style="margin:10px 0">
              <div style="font-weight:600;margin-bottom:4px">${row.label}</div>
              <div style="font-size:10px">Without battery ₹${formatIndian(row.gridCostWithoutBattery)}</div>
              <div style="height:10px;background:#e2e8f0;width:${withoutWidth}%"></div>
              <div style="font-size:10px;margin-top:3px">With battery ₹${formatIndian(row.gridCostWithBattery)}</div>
              <div style="height:10px;background:#059669;width:${withWidth}%"></div>
            </div>`;
        }).join('')}
      </div>
    </div>`;
        html += closePdfPage();
      }

      if (multiUnitSummary) {
        html += openPdfPage();
        const portfolio = multiUnitSummary.portfolio || {};
        const unitRows = multiUnitSummary.unitRows || [];
        const allocationRows = multiUnitSummary.allocationRows || [];
        const totalBalanceMw = allocationRows.reduce((sum, row) => sum + asNumber(balanceMwFromRow(row)), 0);
        const allocatedMwp = valueFrom(portfolio, ['Allocated MWP']);
        const oaUtilizationPct = totalBalanceMw > 0
          ? percentFromValues(allocatedMwp, totalBalanceMw)
          : valueFrom(portfolio, ['OA Utilization %']);
        const lapsedBreakdown = lapsedBreakdownFromPortfolio(portfolio);
        const postBankingLapsedPct = percentFromValues(
          lapsedBreakdown.unitLapsed,
          valueFrom(portfolio, ['Total Generation'])
        ) || valueFrom(portfolio, ['Post Banking Lapsed %']);
        const allocationMetrics = [
          ['Evacuation capacity (MW)', formatIndian(valueFrom(portfolio, ['Evacuation MW', 'Total MWP']))],
          ['Allocated MW', formatIndian(valueFrom(portfolio, ['Allocated MWP']))],
          ['Unallocated MW', formatIndian(valueFrom(portfolio, ['Unallocated MWP']))],
          ['Allocation efficiency', formatPercent(valueFrom(portfolio, ['Allocation Efficiency %']))],
          ['OA utilization', formatPercent(oaUtilizationPct)],
          ['Post-banking replacement', formatPercent(valueFrom(portfolio, ['Post Banking Replacement %', 'Portfolio Replacement %']))],
          ['Post-banking unit lapsed', formatPercent(postBankingLapsedPct)],
          ['Banking settlement', formatIndian0(valueFrom(portfolio, ['Total Banking settlement', 'Banking Settlement']))],
          ['Unit lapsed', formatIndian0(lapsedBreakdown.unitLapsed)],
          ['Unallocated lapsed', formatIndian0(lapsedBreakdown.unallocatedLapsed)],
          ['Total lapsed', formatIndian0(lapsedBreakdown.totalLapsed)],
        ];
        html += `
    <div class="section">
      <h2>Allocation & Banking Summary</h2>
      <div class="grid-container">`;
        allocationMetrics.forEach(([label, value]) => {
          html += `
        <div class="metric-card">
          <div class="metric-label">${label}</div>
          <div class="metric-value">${value}</div>
        </div>`;
        });
        html += `
      </div>
    </div>`;

        if (unitRows.length) {
          html += `
    <div class="section">
      <h2>Post-Banking Unit Performance</h2>
      <div class="table-wrapper">
        <table class="settlement-table">
          <thead>
            <tr>
              <th>Unit</th>
              <th>Allocated MW</th>
              <th>Balance MW</th>
              <th>Balance Utilization</th>
              <th>Replacement</th>
              <th>Unmet</th>
              <th>Lapsed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>`;
          unitRows.forEach((row) => {
            const band = getEfficiencyBand(row);
            const allocatedMw = valueFrom(row, ['Final Allocated MW']);
            const balanceMw = balanceMwFromRow(row);
            const balanceUtilization = percentFromValues(allocatedMw, balanceMw);
            const replacementPct = valueFrom(row, ['Post Banking Replacement %', 'Replacement %']);
            const unmet = valueFrom(row, ['Leftover Demand']);
            const unmetPct = valueFrom(row, ['Unmet %']);
            const lapsed = valueFrom(row, ['Lapsed']);
            const lapsedPct = lapsedPercentFromRow(row);
            html += `
            <tr>
              <td>${row?.['Unit Name'] || row?.['Unit ID'] || '-'}</td>
              <td class="number">${formatIndian(allocatedMw)}</td>
              <td class="number">${formatIndian(balanceMw)}</td>
              <td class="number">${formatPercent(balanceUtilization)}</td>
              <td class="number">${valueWithPercent(valueFrom(row, ['Demand Met']), replacementPct)}</td>
              <td class="number">${valueWithPercent(unmet, unmetPct)}</td>
              <td class="number">${valueWithPercent(lapsed, lapsedPct)}</td>
              <td>${band}</td>
            </tr>`;
          });
          html += `
          </tbody>
        </table>
      </div>
    </div>`;
        }
        html += closePdfPage();
      }

      // Page 5: Banking Summary
      html += openPdfPage();
      if ((bankingSummary?.tod_summary || []).length) {
        html += `
    <div class="section">
      <h2>TOD consumption vs RE units settlement</h2>
      <div class="table-wrapper">
        <table class="settlement-table">
          <thead>
            <tr>
              <th>Time slots</th>
              <th>Consumption</th>
              <th>Demand Met</th>
              <th>% Satisfied</th>
              <th>Lapsed (%)</th>
              <th>Lapsed Units</th>
            </tr>
          </thead>
          <tbody>`;

        const pdfTodRows = bankingSummary.tod_summary || [];

        pdfTodRows.forEach((row, idx) => {
          const isTotal = idx === pdfTodRows.length - 1;
          const label = String(row?.Slot || '').trim() || (bankingSummary?.slot_time_labels?.[row.Slot] || '');
          html += `
          <tr${isTotal ? ' class="total"' : ''}>
            <td>${label}</td>
            <td class="number">${formatIndian0(row.Consumption)}</td>
            <td class="number">${formatIndian0(row.DemandMet)}</td>
            <td class="number">${formatPercent2(row.SatisfiedPct)}</td>
            <td class="number">${formatPercent2(row.LapsedPct)}</td>
            <td class="number${row.LapsedUnits < 0 ? ' negative' : ''}">${formatIndian0(row.LapsedUnits)}</td>
          </tr>`;
        });

        html += `
          </tbody>
        </table>
      </div>
    </div>`;
      }

      const pdfMonthlyRows = getMonthlyBankingRows(bankingSummary);
      if (pdfMonthlyRows.length) {
        const monthlyCols = getMonthlyBankingColumns(bankingSummary);
        html += `
    <div class="section">
      <h2>Monthly consumption vs RE units settlement</h2>
      <div class="table-wrapper">
        <table class="settlement-table">
          <thead>
            <tr>${monthlyCols.map(col => `<th>${col.label}</th>`).join('')}</tr>
          </thead>
          <tbody>`;

        pdfMonthlyRows.forEach((row, idx) => {
          const isTotal = idx === pdfMonthlyRows.length - 1;
          const cells = monthlyCols.map((col) => {
            const rawValue = row?.[col.key];
            const formatted = formatBankingSummaryCell(rawValue, col.type);
            const cls = col.type === 'number' ? ` class="number${Number(rawValue) < 0 ? ' negative' : ''}"` : '';
            return `<td${cls}>${formatted}</td>`;
          }).join('');
          html += `
          <tr${isTotal ? ' class="total"' : ''}>
            ${cells}
          </tr>`;
        });

        html += `
          </tbody>
        </table>
      </div>
    </div>`;
      } else if (!(bankingSummary?.tod_summary || []).length) {
        html += `<div class="meta">Run the Banking node to see the banking summary.</div>`;
      }
      html += closePdfPage();

      // Page 6: Yearwise Tariff
      html += openPdfPage();
      if (opCostsOutput && opCostsOutput.length) {
        const opMetricNameCol = 'Year';
        const opRows = opCostsOutput.filter(r => shouldIncludeOpMetric(r[opMetricNameCol]));
        // Prefer only CAPEX-related rows for the "Yearwise CAPEX" dataframe export.
        const capexRows = opRows.filter(r => /capex/i.test(String(r?.[opMetricNameCol] ?? '')));
        const rowsForCapexTable = capexRows.length ? capexRows : opRows;
        const tbl = buildYearsTopTable(rowsForCapexTable, opMetricNameCol);
        if (tbl) {
          html += `
    <div class="section">
      <h2>Yearwise Tariff</h2>
      <div class="table-wrapper fit-one-page years-top">
        <div class="fit-scale" style="--fit-scale:1">
          <table>
            <thead>${tbl.headerHtml}</thead>
            <tbody>${tbl.bodyHtml}</tbody>
          </table>
        </div>
      </div>
    </div>`;
        }
      } else {
        html += `<div class="meta">Run Operational Costs node to see yearwise tariff.</div>`;
      }
      html += closePdfPage();

      // Page 7: Yearwise Savings
      html += openPdfPage();
      if (financeOutput && financeOutput.length) {
        const financeMetricNameCol = 'Year';
        // Keep the same row selection behavior as before; only change orientation to years-on-top.
        const rowsForSavingsIrrTable = financeOutput.filter(r => shouldIncludeFinanceMetric(r[financeMetricNameCol]));
        const tbl = buildYearsTopTable(rowsForSavingsIrrTable, financeMetricNameCol);
        if (tbl) {
          html += `
    <div class="section">
      <h2>Yearwise Savings</h2>
      <div class="table-wrapper fit-one-page years-top years-savings">
        <div class="fit-scale" style="--fit-scale:1">
          <table>
            <thead>${tbl.headerHtml}</thead>
            <tbody>${tbl.bodyHtml}</tbody>
          </table>
        </div>
      </div>
    </div>`;
        }
      } else {
        html += `<div class="meta">Run Finance node to see yearwise savings.</div>`;
      }
      html += closePdfPage();

      // Page 8: Billing Analytics (optional)
      if (includeBillingAnalyticsInPdf) {
        try {
          html += openPdfPage();
          const snapshot = billingAnalyticsSnapshot;
          if (!snapshot) {
            html += `
    <div class="section" style="break-inside:avoid;page-break-inside:avoid;">
      <h2>Billing Analytics</h2>
      <div class="billing-note">Billing Analytics has not been calculated in the Billing Analytics tab yet.</div>
    </div>`;
          } else {
            const { inputs, calc, todTariffModel, context } = snapshot;
            const { E } = calc;
            const {
              discountRate,
              transmissionLossPerc,
              wheelingLossPerc,
              wheelingRate,
              transmissionRate,
              netSettledUnitsAnnual,
            } = context;
            const fmtMoney = (v) => formatIndian0(v);
            const fmtRate = (v) => {
              const n = Number(v);
              return isFinite(n) ? n.toFixed(2) : '-';
            };
            const thStyle = 'padding:4px 6px;background:#1e293b;color:#fff;font-size:12px;font-weight:600;text-align:left;border:1px solid #334155;white-space:nowrap;';
            const thStyleCenter = 'padding:4px 6px;background:#1e293b;color:#fff;font-size:12px;font-weight:600;text-align:center;border:1px solid #334155;white-space:nowrap;';
            const tdStyle = 'padding:3px 6px;font-size:13px;border:1px solid #e2e8f0;white-space:nowrap;';
            const tdNumStyle = 'padding:3px 6px;font-size:13px;border:1px solid #e2e8f0;text-align:right;font-family:monospace;white-space:nowrap;';
            const tdTotalStyle = 'padding:3px 6px;font-size:13px;border:1px solid #94a3b8;background:#f1f5f9;font-weight:600;white-space:nowrap;';
            const tdTotalNumStyle = 'padding:3px 6px;font-size:13px;border:1px solid #94a3b8;background:#f1f5f9;text-align:right;font-family:monospace;font-weight:600;white-space:nowrap;';

            html += `
    <div class="section" style="break-inside:avoid;page-break-inside:avoid;">
      <h2 style="margin-bottom:6px;font-size:16px;">Billing Analytics</h2>
      <div style="display:flex;gap:12px;align-items:flex-start;flex-wrap:nowrap;">
        <table style="border-collapse:collapse;table-layout:auto;font-size:13px;background:#fff;border:1px solid #cbd5e1;">
          <thead>
            <tr>
              <th style="${thStyle}"></th>
              <th style="${thStyle}"></th>
              <th colspan="2" style="${thStyleCenter}">DISCOM</th>
              <th colspan="2" style="${thStyleCenter}">OA UNITS</th>
              <th colspan="2" style="${thStyleCenter}">EB RESIDUAL</th>
            </tr>
            <tr>
              <th style="${thStyle}">Particular</th>
              <th style="${thStyle}">UOM</th>
              <th style="${thStyle}">Value</th>
              <th style="${thStyle}">Rate</th>
              <th style="${thStyle}">Value</th>
              <th style="${thStyle}">Rate</th>
              <th style="${thStyle}">Value</th>
              <th style="${thStyle}">Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style="${tdStyle}">Voltage</td><td style="${tdStyle}">KV</td><td style="${tdNumStyle}">${inputs.voltage || '-'}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">CD</td><td style="${tdStyle}">KVA</td><td style="${tdNumStyle}">${formatIndian0(E.cd)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td></tr>
            <tr><td style="${tdStyle}">Actual CD</td><td style="${tdStyle}">KVA</td><td style="${tdNumStyle}">${formatIndian0(E.actualCd)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Billed CD</td><td style="${tdStyle}">KVA</td><td style="${tdNumStyle}">${formatIndian0(E.billedCd)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${formatIndian0(calc.I10)}</td><td style="${tdNumStyle}">-</td></tr>
            <tr><td style="${tdStyle}">Units consumed</td><td style="${tdStyle}">Units</td><td style="${tdNumStyle}">${formatIndian0(E.units)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${formatIndian0(calc.I11)}</td><td style="${tdNumStyle}">-</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Units consumed</td><td style="${tdStyle}">KVAH</td><td style="${tdNumStyle}">${formatIndian0(E.kvah)}</td><td style="${tdNumStyle}">${fmtRate(calc.F12)}</td><td style="${tdNumStyle}">${formatIndian0(calc.G12)}</td><td style="${tdNumStyle}">${fmtRate(calc.oaUnitsRateH12)}</td><td style="${tdNumStyle}">${formatIndian0(calc.I12)}</td><td style="${tdNumStyle}">-</td></tr>
            <tr><td style="${tdStyle}">Demand charge</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.demandCharge)}</td><td style="${tdNumStyle}">${fmtRate(calc.J13)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I13)}</td><td style="${tdNumStyle}">${fmtRate(calc.J13)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Wheeling charge</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(calc.wheelingCharge)}</td><td style="${tdNumStyle}">${fmtRate(wheelingRate)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I14)}</td><td style="${tdNumStyle}">${fmtRate(calc.J14)}</td></tr>
            <tr><td style="${tdStyle}">Energy charge</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(calc.energyCharge)}</td><td style="${tdNumStyle}">${fmtRate(calc.J15)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I15)}</td><td style="${tdNumStyle}">${fmtRate(calc.J15)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">TOD</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.tod)}</td><td style="${tdNumStyle}">${fmtRate(calc.F16)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I16)}</td><td style="${tdNumStyle}">${fmtRate(calc.J16)}</td></tr>
            <tr><td style="${tdStyle}">FAC</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.fac)}</td><td style="${tdNumStyle}">${fmtRate(calc.F17)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I17)}</td><td style="${tdNumStyle}">${fmtRate(calc.J17)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">ED</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.ed)}</td><td style="${tdNumStyle}">${fmtRate(calc.F18)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I18)}</td><td style="${tdNumStyle}">${fmtRate(calc.J18)}</td></tr>
            <tr><td style="${tdStyle}">Tax on sale</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.taxOnSale)}</td><td style="${tdNumStyle}">${fmtRate(calc.F19)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I19)}</td><td style="${tdNumStyle}">${fmtRate(calc.J19)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Tax Collection at Source</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.tcs)}</td><td style="${tdNumStyle}">${fmtRate(calc.F20)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I20)}</td><td style="${tdNumStyle}">${fmtRate(calc.J20)}</td></tr>
            <tr><td style="${tdStyle}">Bulk Consumption rebate</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.bulkRebate)}</td><td style="${tdNumStyle}">${fmtRate(calc.F21)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I21)}</td><td style="${tdNumStyle}">${fmtRate(calc.J21)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Prompt payment discount</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.promptDiscount)}</td><td style="${tdNumStyle}">${fmtRate(calc.F22)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I22)}</td><td style="${tdNumStyle}">${fmtRate(calc.J22)}</td></tr>
            <tr><td style="${tdStyle}">Load factor incentive</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.loadFactorIncentive)}</td><td style="${tdNumStyle}">${fmtRate(calc.F23)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I23)}</td><td style="${tdNumStyle}">${fmtRate(calc.J23)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Incremental consumption rebate</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.incrRebate)}</td><td style="${tdNumStyle}">${fmtRate(calc.F24)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I24)}</td><td style="${tdNumStyle}">${fmtRate(calc.J24)}</td></tr>
            <tr><td style="${tdStyle}">Regional subsidy</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.regionalSubsidy)}</td><td style="${tdNumStyle}">${fmtRate(calc.F25)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtRate(calc.J25)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">${String(calc.universalSubsidyLabel || E.universalSubsidyLabel || 'Universal subsidy').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td><td style="${tdStyle}">INR</td><td style="${tdNumStyle}">${fmtMoney(E.universalSubsidy)}</td><td style="${tdNumStyle}">${fmtRate(calc.universalSubsidyRate)}</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtRate(calc.universalSubsidyRate)}</td></tr>
            <tr><td style="${tdTotalStyle}"><b>Total Power cost</b></td><td style="${tdTotalStyle}">INR</td><td style="${tdTotalNumStyle}"><b>${fmtMoney(calc.totalPowerCostE26)}</b></td><td style="${tdTotalNumStyle}"><b>${fmtRate(calc.F26)}</b></td><td style="${tdTotalNumStyle}"><b>${fmtMoney(calc.G26)}</b></td><td style="${tdTotalNumStyle}">-</td><td style="${tdTotalNumStyle}"><b>${fmtMoney(calc.I26)}</b></td><td style="${tdTotalNumStyle}"><b>${fmtRate(calc.J26)}</b></td></tr>
            <tr style="background:#fef3c7"><td style="${tdStyle}"><b>Replaceable Power cost</b></td><td style="${tdStyle}">INR/unit</td><td style="${tdNumStyle};background:#fef3c7;font-weight:700;"><b>${fmtRate(calc.replaceablePowerCostE27)}</b></td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtMoney(calc.I27)}</td><td style="${tdNumStyle}">${fmtRate(calc.J27)}</td></tr>
            <tr><td style="${tdStyle}">Savings on total power consumed</td><td style="${tdStyle}">INR/unit</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtRate(calc.J28)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Savings on OA units</td><td style="${tdStyle}">INR/unit</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">-</td><td style="${tdNumStyle}">${fmtRate(calc.savingsOnOaUnits)}</td></tr>
          </tbody>
        </table>
        ${includeOperationalCostInPdf ? `
        <table style="border-collapse:collapse;table-layout:auto;font-size:13px;background:#fff;border:1px solid #cbd5e1;align-self:flex-start;">
          <thead>
            <tr>
              <th style="${thStyle}">Operational Cost for Open Access</th>
              <th style="${thStyle}">UOM</th>
              <th style="${thStyle}">Charges</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style="${tdStyle}">Generation tariff</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(calc.generationTariff)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Current Replacement Tariff</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(calc.replaceablePowerCostE27)}</td></tr>
            <tr><td style="${tdStyle}">Transmission charge in</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(transmissionRate)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Transmission loss in Rs / unit</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(calc.oc.transmissionLoss)}</td></tr>
            <tr><td style="${tdStyle}">Wheeling charges in Rs unit</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(wheelingRate)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Wheeling losses in Rs per unit</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(calc.oc.wheelingLoss)}</td></tr>
            <tr><td style="${tdStyle}">Banking Charge on Banked units</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(calc.oc.bankingCharge)}</td></tr>
            <tr style="background:#fafbfc"><td style="${tdStyle}">Lapse unit charge</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(calc.oc.lapsedUnitCharge)}</td></tr>
            <tr><td style="${tdStyle}">O&amp;M, lease, Insurance and other expenses</td><td style="${tdStyle}">Rs/unit</td><td style="${tdNumStyle}">${fmtRate(calc.oc.omLeaseInsuranceOther)}</td></tr>
            <tr style="background:#d1fae5"><td style="${tdTotalStyle};background:#d1fae5;"><b>Delivered power cost at Consumption point</b></td><td style="${tdTotalStyle};background:#d1fae5;">Rs/unit</td><td style="${tdTotalNumStyle};background:#d1fae5;"><b>${fmtRate(calc.deliveredPowerCostH12)}</b></td></tr>
          </tbody>
        </table>` : ''}
      </div>
      ${includeTodWiseTariffInPdf ? `
      <div style="margin-top:10px;">
        <table style="border-collapse:collapse;table-layout:auto;font-size:13px;background:#fff;border:1px solid #cbd5e1;">
          <thead>
            <tr>
              <th style="${thStyle}">Time slots</th>
              <th style="${thStyle}">Consumption</th>
              <th style="${thStyle}">Demand Met</th>
              <th style="${thStyle}">Grid consumption</th>
              <th style="${thStyle}">TOD tariff (Rs/unit)</th>
            </tr>
          </thead>
          <tbody>
            ${todTariffModel.hasData ? (todTariffModel.rows || []).map((row, index) => `
            <tr${index % 2 ? ' style="background:#fafbfc"' : ''}>
              <td style="${tdStyle}">${row.label}</td>
              <td style="${tdNumStyle}">${formatIndian0(row.consumption)}</td>
              <td style="${tdNumStyle}">${formatIndian0(row.demandMet)}</td>
              <td style="${tdNumStyle}">${formatIndian0(row.gridConsumption)}</td>
              <td style="${tdNumStyle}">${fmtRate(row.tariff)}</td>
            </tr>`).join('') : `
            <tr>
              <td colspan="5" style="${tdStyle}">No TOD time-slot summary available.</td>
            </tr>`}
            ${todTariffModel.hasData ? `
            <tr>
              <td style="${tdTotalStyle}"><b>Total</b></td>
              <td style="${tdTotalNumStyle}"><b>${formatIndian0(todTariffModel.totals.totalConsumption)}</b></td>
              <td style="${tdTotalNumStyle}"><b>${formatIndian0(todTariffModel.totals.totalDemandMet)}</b></td>
              <td style="${tdTotalNumStyle}"><b>${formatIndian0(todTariffModel.totals.totalGridConsumption)}</b></td>
              <td style="${tdTotalNumStyle}"><b>${fmtRate(todTariffModel.totals.weightedAvgTariff)}</b></td>
            </tr>` : ''}
          </tbody>
        </table>
      </div>` : ''}
      <div style="margin-top:8px;font-size:11px;color:#475569;">
        Discount rate: ${fmtRate(discountRate)} | Wheeling charges: ${fmtRate(wheelingRate)} | Transmission charges: ${fmtRate(transmissionRate)} | Transmission loss: ${fmtRate(transmissionLossPerc)} | Wheeling loss: ${fmtRate(wheelingLossPerc)} | Net settled units (annual): ${formatIndian0(netSettledUnitsAnnual)}
      </div>
    </div>`;
          }
        } catch (e) {
          html += `
    <div class="section" style="break-inside:avoid;page-break-inside:avoid;">
      <h2>Billing Analytics</h2>
      <div class="billing-note">Billing Analytics export failed: ${(e && e.message) ? e.message : 'Unknown error'}</div>
    </div>`;
        } finally {
          html += closePdfPage();
        }
      }

      html += `
  <script>
    (function () {
      function setPageNumber() {
        try {
          var el = document.querySelector('.pageNumber');
          if (el) el.textContent = String((window.location && window.location.hash && window.location.hash.replace('#','')) || '');
        } catch (e) {}
      }

      // Dynamically scale each "fit-one-page" table to fit BOTH width and height of a single printed page,
      // while preserving ALL columns (no truncation) and avoiding spill to the next page.
      function fitOnePageTables() {
        try {
          // px-per-mm helper for more stable page height calculations
          function pxPerMm() {
            var el = document.createElement('div');
            el.style.width = '100mm';
            el.style.position = 'absolute';
            el.style.left = '-1000mm';
            document.body.appendChild(el);
            var px = el.getBoundingClientRect().width / 100;
            document.body.removeChild(el);
            return px || 3.78; // fallback
          }
          var ppm = pxPerMm();
          // A4 landscape height = 210mm; subtract @page margins (6mm top + 6mm bottom)
          var pageUsableHeight = (210 - 12) * ppm;
          var footer = document.querySelector('.report-footer');
          var footerH = footer ? footer.getBoundingClientRect().height : 0;

          var wrappers = document.querySelectorAll('.table-wrapper.fit-one-page');
          wrappers.forEach(function (wrap) {
            var scaler = wrap.querySelector('.fit-scale');
            if (!scaler) return;
            var isYearsTop = wrap.classList && wrap.classList.contains('years-top');
            var isSavings = wrap.classList && wrap.classList.contains('years-savings');
            // Measure single-table wrappers by the table itself (captures true overflow width),
            // but multi-table wrappers by the scaler container (Billing Analytics).
            var tables = scaler.querySelectorAll('table');
            var singleTable = (tables && tables.length === 1) ? tables[0] : null;
            var measureTarget = singleTable || scaler;

            // reset scale before measuring
            scaler.style.setProperty('--fit-scale', '1');

            // measure unscaled table width/height
            var rect = measureTarget.getBoundingClientRect();
            var contentWidth = measureTarget.scrollWidth || rect.width || 0;
            var contentHeight = measureTarget.scrollHeight || rect.height || 0;
            // For multi-table pages, use the widest table for width (scaler.scrollWidth can lie due to 100% widths).
            if (!singleTable && tables && tables.length) {
              var maxW = 0;
              tables.forEach(function (t) {
                var tr = t.getBoundingClientRect();
                var w = t.scrollWidth || tr.width || 0;
                if (w > maxW) maxW = w;
              });
              if (maxW > contentWidth) contentWidth = maxW;
            }
            var wrapRect = wrap.getBoundingClientRect();
            var wrapWidth = wrap.clientWidth || wrapRect.width || 0;
            if (!contentWidth || !wrapWidth || !contentHeight) return;

            // Width scale (fill page width)
            var scaleW = wrapWidth / contentWidth;
            if (!isFinite(scaleW) || scaleW <= 0) scaleW = 1;

            // Height scale: only apply for non-yearwise tables.
            // Yearwise tables are explicitly column-sized to fit horizontally; height-based scaling
            // causes inconsistent sizing when row counts vary (e.g., Savings table has fewer rows than Tariff).
            var scale = scaleW;
            if (!isYearsTop) {
              // Compute how much vertical space this section has on a printed page.
              var sec = wrap.closest('.section');
              var title = sec ? sec.querySelector('h2') : null;
              var titleH = title ? title.getBoundingClientRect().height : 0;
              var chromeH = titleH + 14; // title + small gap
              var availableH = Math.max(50, pageUsableHeight - footerH - chromeH);
              var scaleH = availableH / contentHeight;
              if (!isFinite(scaleH) || scaleH <= 0) scaleH = 1;
              // Final scale must satisfy BOTH constraints (no spill).
              scale = Math.min(scaleW, scaleH);
            }
            
            // For yearwise tables: allow AGGRESSIVE upscaling to fill the page since they're compact
            if (isYearsTop) {
              // Allow more upscale for Savings table to better fill the page
              var maxScale = isSavings ? 2.6 : 2.0;
              if (scale > maxScale) scale = maxScale;
              // Minimal safety margin for yearwise - they're pre-sized to fit
              scale = Math.max(0.1, scale - 0.0002);
            } else {
              // Other tables: more conservative upscale limit
              if (scale > 1.12) scale = 1.12;
              // Tiny safety margin so it doesn't clip due to rounding in print
              scale = Math.max(0.1, scale - 0.001);
            }

            scaler.style.setProperty('--fit-scale', String(scale));
          });
        } catch (e) {}
      }

      setPageNumber();
      // Initial fit after layout
      setTimeout(fitOnePageTables, 0);
      // Re-fit before print (print preview can change layout metrics)
      window.addEventListener('beforeprint', fitOnePageTables);
      window.addEventListener('resize', function () { setTimeout(fitOnePageTables, 50); });
    })();
  </script>
</body>
</html>`;

      const filename = makeSummaryPdfFilename();
      try {
        const pdfBlob = await exportSummaryPdf({ html, filename });
        downloadBlob(pdfBlob, filename);
        toast.success('PDF downloaded.', {
          position: 'bottom-right',
          autoClose: 4000,
          transition: Bounce,
        });
        return;
      } catch (serverErr) {
        console.warn('Server PDF export failed; falling back to browser print.', serverErr);
        printSummaryHtmlFallback(html);
      }

      toast.info('Server PDF export unavailable. Use the print dialog to save it.', {
        position: 'bottom-right',
        autoClose: 4000,
        transition: Bounce,
      });
    } catch (err) {
      console.error('Error generating summary HTML:', err);
      toast.error(`Failed to generate HTML: ${err.message}`, {
        position: 'bottom-right',
        autoClose: 5000,
        transition: Bounce,
      });
    }
  };

  // Assumptions-only HTML Download Handler (prints to PDF-friendly window)
  const handleDownloadAssumptionsPdf = async () => {
    try {
      const logoDataUrl = await getIntegrumLogoDataUrl();
      const pipelineTitle = session.pipeline_name || 'Pipeline';
      const generatedAt = new Date();
      // Build HTML content (same base styles as summary, but only assumptions)
      let html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Project Assumptions - ${pipelineTitle}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    :root{
      --brand-ink:#0b3a5a;
      --brand-gold:#f2b705;
      --text:#111827;
      --muted:#6b7280;
      --border:#e5e7eb;
      --bg:#f9fafb;
      --panel:#ffffff;
    }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: var(--text);
      padding: 0;
      background: var(--bg);
    }
    .report-header{
      position: relative;
      background: #fff;
      border-bottom: 2px solid var(--brand-ink);
      padding: 8mm 8mm 5mm 8mm;
      margin-bottom: 6mm;
    }
    .report-header-inner{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 16px;
    }
    .brand-block{
      display:flex;
      flex-direction:column;
      gap: 3px;
      min-width: 0;
    }
    .report-title{
      font-size: 20px;
      line-height: 1.15;
      font-weight: 800;
      letter-spacing: 0.2px;
      color: var(--brand-ink);
      margin: 0;
    }
    .report-subtitle{
      font-size: 12px;
      color: var(--muted);
      margin-top: 2px;
    }
    .brand-logo{
      width: 155px;
      height: auto;
      object-fit: contain;
    }
    .report-footer{
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: #fff;
      border-top: 1px solid var(--border);
      padding: 3mm 8mm;
      font-size: 10px;
      color: var(--muted);
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 10px;
    }
    /* Footer is fixed; reserve space so content doesn't overlap it */
    :root{
      --footer-offset: 14mm;
    }
    .page {
      padding: 8mm 8mm var(--footer-offset) 8mm;
    }
    .container { 
      width: 100%;
      max-width: 100%;
      margin: 0 auto; 
      background: var(--panel);
      padding: 12px 12px 8px 12px;
      border: 1px solid var(--border);
      border-radius: 10px;
    }
    .meta { 
      color: var(--muted); 
      margin-bottom: 16px;
      font-size: 12px;
    }
    h2 { 
      font-size: 14px; 
      margin: 8px 0 4px 0;
      color: var(--brand-ink);
      padding: 6px 10px;
      background: #f3f4f6;
      border-left: 4px solid var(--brand-gold);
    }
    .table-wrapper {
      width: 100%;
      overflow-x: visible;
      margin-bottom: 8px;
      border: 1px solid var(--border);
      border-radius: 8px;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      font-size: 10px;
      background: white;
      table-layout: fixed;
    }
    th, td { 
      padding: 4px 6px; 
      text-align: left; 
      border: 1px solid var(--border);
      white-space: normal;
      word-break: break-word;
    }
    th { 
      background: var(--brand-ink);
      color: white;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 9px;
      letter-spacing: 0.01em;
      line-height: 1.3;
      white-space: normal;
      word-break: break-word;
    }
    td.number { 
      text-align: right; 
      font-variant-numeric: tabular-nums;
      font-weight: 500;
    }
    tr.total td { 
      font-weight: 700; 
      background: #e5e7eb;
      border-top: 2px solid #9ca3af;
    }
    tr.section-row td { font-weight: 800; background: #f3f4f6; color: var(--brand-ink); }
    .assump-section { break-inside: avoid; page-break-inside: avoid; margin-top: 6px; }
    .assump-section:first-child { margin-top: 0; }
    /* Two-up layout (Loan + Lease) */
    .assump-two-col{
      display: flex;
      gap: 12px;
      align-items: flex-start;
      break-inside: avoid;
      page-break-inside: avoid;
      margin-top: 6px;
    }
    .assump-two-col:first-child{ margin-top: 0; }
    .assump-two-col .assump-section{
      flex: 1 1 0;
      min-width: 0;
      margin-top: 0;
    }
    .maintenance-table{ table-layout: fixed; width: 100%; }
    .maintenance-table th:nth-child(1), .maintenance-table td:nth-child(1){ width: 32%; }
    .maintenance-table th:nth-child(2), .maintenance-table td:nth-child(2){ width: 26%; }
    .maintenance-table th:nth-child(3), .maintenance-table td:nth-child(3){ width: 22%; }
    .maintenance-table th:nth-child(4), .maintenance-table td:nth-child(4){ width: 20%; }
    .colhead td { background:var(--brand-ink); color:white; font-weight:600; text-transform:uppercase; font-size:9px; letter-spacing:0.01em; }
    .compact-table { table-layout: fixed; }
    .compact-table th.particular, .compact-table td.particular { width: 60%; }
    .compact-table th.value, .compact-table td.value { width: 40%; }
    .compact-table td.value { text-align: right; font-variant-numeric: tabular-nums; }
    .compact-table td.value.text { text-align: left; }
    tr, td { break-inside: avoid; page-break-inside: avoid; }
    @media print {
      @page { size: A4 portrait; margin: 8mm; }
      body { background: #fff; }
      .page { padding: 0 0 var(--footer-offset) 0; }
      .report-header{ margin-bottom: 5mm; }
    }
  </style>
</head>
<body>
  <div class="report-footer">
    <div>Integrum Energy • Internal Use</div>
    <div>Page <span class="pageNumber"></span></div>
  </div>
  <div class="page">
    <div class="report-header">
      <div class="report-header-inner">
        <div class="brand-block">
          <div class="report-title">Project Assumptions</div>
          <div class="report-subtitle">Pipeline: ${pipelineTitle}</div>
          <div class="report-subtitle">Generated: ${generatedAt.toLocaleString()}</div>
        </div>
        <img class="brand-logo" src="${logoDataUrl}" alt="Integrum Energy" />
      </div>
    </div>
    <div class="container">
`;

      // Insert assumptions section
      html += buildAssumptionsExportSectionsHtml();

      html += `
    </div>
  </div>
  <script>
    (function () {
      try {
        var el = document.querySelector('.pageNumber');
        if (el) el.textContent = String((window.location && window.location.hash && window.location.hash.replace('#','')) || '');
      } catch (e) {}
    })();
  </script>
</body>
</html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      const cleanup = () => {
        URL.revokeObjectURL(url);
        if (printWindow && !printWindow.closed) {
          printWindow.close();
        }
      };
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
          setTimeout(cleanup, 500);
        };
      } else {
        cleanup();
        throw new Error('Popup blocked. Allow popups to download PDF.');
      }

      toast.success('Assumptions PDF ready. Use the print dialog to save it.', {
        position: 'bottom-right',
        autoClose: 4000,
        transition: Bounce,
      });
    } catch (err) {
      console.error('Error generating assumptions HTML:', err);
      toast.error(`Failed to generate assumptions HTML: ${err.message}`, {
        position: 'bottom-right',
        autoClose: 5000,
        transition: Bounce,
      });
    }
  };

  // --- Display name overrides (edit these labels as you wish) ---
  const financeCardNameOverrides = {
    "Avg_Annual_Savings": "Average Annual Savings (Cr)",
    "Avg_Savings_During_Debt": "Average Savings (Debt) (Cr)",
    "Avg_ROE (%)": "Average ROE (%)",
    "Equity_IRR": "Equity IRR (%)",
    "Avg_ROI (%)": "Average ROI (%)",
    "Project_IRR (%)": "Project IRR (%)",
    "Pay_Back_Period": "Payback Period (yrs)",
    "Total_Cost": "Total Cost (Cr)",
    "Discom_Cost": "Discom Cost (Cr)"
  };

  const opCardNameOverrides = {
    "Consumer Effective Levellised Cost (Landed cost) in Debt": "Landed Cost (Debt)",
    "Consumer Effective Levellised Cost (Landed cost) Post Debt": "Landed Cost (Post-Debt)",
    "Consumer Effective Levellised Cost of Generation in Debt": "Gen Cost (Debt)",
    "Consumer Effective Levellised Cost of Generation Post Debt": "Gen Cost (Post-Debt)",
    "Consumer Effective Levellised Cost of Generation": "Gen Cost (Overall)",
    "Consumer Effective Levellised Cost (Landed cost)": "Landed Cost (Overall)"
  };

  const getFinanceLastSevenCards = () => {
    if (!financeOutput || financeOutput.length === 0) return [];
    const lastSeven = financeOutput.slice(-7);
    return lastSeven.map(row => {
      const rawTitle = row['Year'];
      const title = financeCardNameOverrides[rawTitle] ?? rawTitle;
      // Use first numeric column value
      const value = Object.keys(row)
        .filter(k => k !== 'Year')
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(k => row[k])?.[0];
      return { title, rawTitle, value: format2(value) };
    });
  };

  const getFinanceChartData = () => {
    if (!financeOutput || financeOutput.length === 0) return null;
    const findRowByTitle = (title) => financeOutput.find(r => (r['Year'] || '').toString().toLowerCase() === title.toLowerCase());
    const totalRow = findRowByTitle('Total_Cost');
    const discomRow = findRowByTitle('Discom_Cost');
    if (!totalRow || !discomRow) return null;
    const numericKeys = Object.keys(totalRow)
      .filter(k => k !== 'Year' && /^\d+$/.test(k))
      .sort((a, b) => parseInt(a) - parseInt(b));
    const labels = numericKeys.map(k => k);
    const totalData = numericKeys.map(k => round2num(totalRow[k]));
    const discomData = numericKeys.map(k => round2num(discomRow[k]));
    return {
      labels,
      datasets: [
        { label: 'Total Cost', data: totalData, borderColor: 'rgba(54, 162, 235, 1)', backgroundColor: 'rgba(54, 162, 235, 0.2)', tension: 0.2 },
        { label: 'Discom Cost', data: discomData, borderColor: 'rgba(255, 99, 132, 1)', backgroundColor: 'rgba(255, 99, 132, 0.2)', tension: 0.2 }
      ]
    };
  };

  const getOperationalCards = () => {
    if (!opCostsOutput || opCostsOutput.length === 0) return [];
    const asValue = (row) => {
      if (!row) return null;
      const firstNum = Object.keys(row)
        .filter(k => k !== 'Year')
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(k => row[k])?.[0];
      return firstNum;
    };
    const byTitle = (t) => opCostsOutput.find(r => (r['Year'] || '').toString().toLowerCase() === t.toLowerCase());

    const wanted = [
      // Generation costs (overall, in debt, post debt)
      'Consumer Effective Levellised Cost of Generation',
      'Consumer Effective Levellised Cost of Generation in Debt',
      'Consumer Effective Levellised Cost of Generation Post Debt',
      // Landed costs (overall, in debt, post debt)
      'Consumer Effective Levellised Cost (Landed cost)',
      'Consumer Effective Levellised Cost (Landed cost) in Debt',
      'Consumer Effective Levellised Cost (Landed cost) Post Debt'
    ];

    return wanted.map(key => {
      const row = byTitle(key);
      if (!row) return null;
      const displayTitle = opCardNameOverrides[key] ?? key;
      return { title: displayTitle, rawTitle: key, value: format2(asValue(row)) };
    }).filter(Boolean);
  };

  // --- Visualization builders using step3Monthly ---
  const getAllSlots = () => {
    const slotCol = step3Monthly.columns.find(c => c.toLowerCase() === 'slot');
    if (!slotCol) return [];
    const vals = Array.from(new Set((step3Monthly.rows || []).map(r => r[slotCol])));
    return vals;
  };
  const getAllMonths = () => {
    const monthCol = step3Monthly.columns.find(c => c.toLowerCase() === 'month');
    if (!monthCol) return [];
    const vals = Array.from(new Set((step3Monthly.rows || []).map(r => r[monthCol])));
    return vals.sort((a,b)=>Number(a)-Number(b));
  };

  const buildSlotLineData = () => {
    const slotCol = step3Monthly.columns.find(c => c.toLowerCase() === 'slot');
    const monthCol = step3Monthly.columns.find(c => c.toLowerCase() === 'month');
    const replCol = step3Monthly.columns.find(c => c.toLowerCase() === 'replacement');
    const consCol = step3Monthly.columns.find(c => c.toLowerCase() === 'consumption');
    if (!slotCol || !monthCol || !replCol || !consCol || !selectedSlot) return null;
    const rows = (step3Monthly.rows || []).filter(r => (r[slotCol] || '').toString() === selectedSlot.toString());
    const byMonth = {};
    rows.forEach(r => {
      const m = r[monthCol];
      byMonth[m] = byMonth[m] || { replacement: 0, consumption: 0 };
      byMonth[m].replacement += Number(r[replCol]) || 0;
      byMonth[m].consumption += Number(r[consCol]) || 0;
    });
    const labels = Object.keys(byMonth).sort((a,b)=>Number(a)-Number(b));
    return {
      labels,
      datasets: [
        { label: 'Consumption', data: labels.map(m => byMonth[m].consumption), borderColor: '#16a34a', backgroundColor: 'rgba(22,163,74,0.2)', tension: 0.2 },
        { label: 'Demand met', data: labels.map(m => byMonth[m].replacement), borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.2)', tension: 0.2 },
      ]
    };
  };

  const buildMonthBarData = () => {
    const slotCol = step3Monthly.columns.find(c => c.toLowerCase() === 'slot');
    const monthCol = step3Monthly.columns.find(c => c.toLowerCase() === 'month');
    const replCol = step3Monthly.columns.find(c => c.toLowerCase() === 'replacement');
    const consCol = step3Monthly.columns.find(c => c.toLowerCase() === 'consumption');
    if (!slotCol || !monthCol || !replCol || !consCol) return null;
    const rows = step3Monthly.rows || [];
    const slots = Array.from(new Set(rows.map(r => r[slotCol])));
    const months = Array.from(new Set(rows.map(r => r[monthCol]))).sort((a,b)=>Number(a)-Number(b));
    const monthSlotAgg = {};
    months.forEach(m => { monthSlotAgg[m] = {}; slots.forEach(s => { monthSlotAgg[m][s] = { consumption:0, replacement:0 }; }); });
    rows.forEach(r => {
      const m = r[monthCol];
      const s = r[slotCol];
      if (monthSlotAgg[m] && monthSlotAgg[m][s]) {
        monthSlotAgg[m][s].consumption += Number(r[consCol]) || 0;
        monthSlotAgg[m][s].replacement += Number(r[replCol]) || 0;
      }
    });
    const consumptionColors = ['#2563eb','#1d4ed8','#0ea5e9','#06b6d4','#0891b2','#155e75','#38bdf8','#3b82f6'];
    const demandColors = ['#f59e0b','#ea580c','#d97706','#f97316','#fb923c','#ef4444','#dc2626','#fbbf24'];
    const consDatasets = slots.map((s, idx) => ({
      label: `${s} - Consumption`,
      stack: 'consumption',
      backgroundColor: consumptionColors[idx % consumptionColors.length],
      borderColor: '#1e3a8a',
      borderWidth: 1,
      data: months.map(m => monthSlotAgg[m][s].consumption)
    }));
    const replDatasets = slots.map((s, idx) => ({
      label: `${s} - Demand met`,
      stack: 'replacement',
      backgroundColor: demandColors[idx % demandColors.length],
      borderColor: '#b45309',
      borderWidth: 1,
      data: months.map(m => monthSlotAgg[m][s].replacement)
    }));
    return {
      labels: months,
      datasets: [...consDatasets, ...replDatasets]
    };
  };

  const getDonutData = (labelA, valueA, labelB, totalB, color = '#22c55e') => {
    const a = Number(valueA) || 0;
    const b = Number(totalB) || 0;
    const rest = Math.max(b - a, 0);
    if (a <= 0 && b <= 0) return null;
    return {
      labels: [labelA, `${labelB} (remaining)`],
      datasets: [
        {
          data: [a, rest],
          backgroundColor: [color, '#e5e7eb'],
          hoverBackgroundColor: [color, '#d1d5db'],
          borderWidth: 1,
        },
      ],
    };
  };

  const smallDonutOptions = {
    cutout: '68%',
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 10, boxHeight: 10, font: { size: 10 } } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a,b)=>a+b,0) || 1;
            const val = Number(ctx.parsed) || 0;
            const pct = ((val/total)*100).toFixed(1);
            return `${ctx.label}: ${formatIndian(val)} (${pct}%)`;
          }
        }
      }
    },
    maintainAspectRatio: false,
  };

  const asNumber = (value) => {
    const n = Number(value);
    return isFinite(n) ? n : 0;
  };

  const clampPercent = (value) => Math.max(0, Math.min(100, asNumber(value)));

  const valueFrom = (row, keys) => {
    for (const key of keys) {
      if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== '') return row[key];
    }
    return 0;
  };

  const balanceMwFromRow = (row) => {
    if (row && row['Balance MW'] !== undefined && row['Balance MW'] !== null && row['Balance MW'] !== '') {
      return row['Balance MW'];
    }
    return asNumber(row?.Balance);
  };

  const lapsedBreakdownFromPortfolio = (portfolio) => {
    const unallocatedLapsed = asNumber(valueFrom(portfolio, ['Unallocated Lapsed']));
    const hasUnitLapsed = portfolio?.['Unit Lapsed'] !== undefined
      && portfolio?.['Unit Lapsed'] !== null
      && portfolio?.['Unit Lapsed'] !== '';
    const unitLapsed = hasUnitLapsed
      ? asNumber(portfolio['Unit Lapsed'])
      : asNumber(valueFrom(portfolio, ['Total Lapsed'])) - unallocatedLapsed;
    return {
      unitLapsed,
      unallocatedLapsed,
      totalLapsed: unitLapsed + unallocatedLapsed,
    };
  };

  const percentFromValues = (numerator, denominator) => {
    const den = asNumber(denominator);
    if (den <= 0) return 0;
    return (asNumber(numerator) / den) * 100;
  };

  const lapsedPercentFromRow = (row) => {
    const explicit = valueFrom(row, ['Lapsed %']);
    const totalGeneration = valueFrom(row, ['Total Generation']);
    if (asNumber(totalGeneration) > 0) {
      return percentFromValues(valueFrom(row, ['Lapsed']), totalGeneration);
    }
    return explicit;
  };

  const valueWithPercent = (value, percent, formatter = formatIndian0) =>
    `${formatter(value)} (${formatPercent(percent)})`;

  const getEfficiencyBand = (row) => {
    const explicitBand = row?.['Efficiency Band'];
    const replacement = asNumber(valueFrom(row, ['Post Banking Replacement %', 'Replacement %']));
    const lapsed = asNumber(lapsedPercentFromRow(row));
    const unmet = asNumber(row?.['Unmet %']);
    if (!replacement && !lapsed && !unmet && explicitBand) return String(explicitBand);
    if (replacement >= 85 && lapsed <= 10 && unmet <= 15) return 'Good';
    if (replacement >= 65 && lapsed <= 25 && unmet <= 35) return 'Watch';
    return 'Poor';
  };

  const getBandClass = (band) => {
    const normalized = String(band || '').toLowerCase();
    if (normalized === 'good') return 'good';
    if (normalized === 'watch') return 'watch';
    return 'poor';
  };

  const getMultiUnitAllocationBarData = () => {
    const rows = multiUnitSummary?.allocationRows?.length
      ? multiUnitSummary.allocationRows
      : (multiUnitSummary?.unitRows || []);
    if (!rows.length) return null;
    return {
      labels: rows.map((row) => row?.['Unit Name'] || row?.['Unit ID'] || 'Unit'),
      datasets: [
        {
          label: 'Balance',
          data: rows.map((row) => asNumber(balanceMwFromRow(row))),
          backgroundColor: 'rgba(148, 163, 184, 0.45)',
          borderColor: '#94a3b8',
          borderWidth: 1,
        },
        {
          label: 'Allocated MW',
          data: rows.map((row) => asNumber(row?.['Final Allocated MW'])),
          backgroundColor: 'rgba(5, 150, 105, 0.75)',
          borderColor: '#059669',
          borderWidth: 1,
        },
      ],
    };
  };

  const multiUnitBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
    },
    scales: {
      x: { ticks: { maxRotation: 35, minRotation: 0 } },
      y: { title: { display: true, text: 'MW' }, beginAtZero: true },
    },
  };

  const renderProgressMetric = ({ label, value, suffix = '%', hint }) => {
    const pct = clampPercent(value);
    return (
      <div className="allocation-progress-card">
        <div className="allocation-progress-header">
          <span>{label}</span>
          <strong>{suffix === '%' ? formatPercent(pct) : `${formatIndian(value)}${suffix}`}</strong>
        </div>
        {hint && <div className="allocation-progress-hint">{hint}</div>}
        <div className="allocation-progress-track">
          <div className="allocation-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  const renderMultiUnitSummaryTab = () => {
    if (!multiUnitSummary) {
      return (
        <div className="summary-section span-2">
          <h3 className="section-title">Allocation & Banking</h3>
          <div className="summary-empty">Run a multi-unit Matcher followed by Banking to see allocation and post-banking efficiency.</div>
        </div>
      );
    }

    const portfolio = multiUnitSummary.portfolio || {};
    const unitRows = multiUnitSummary.unitRows || [];
    const barData = getMultiUnitAllocationBarData();
    const allocationRows = multiUnitSummary.allocationRows || [];
    const allocationSource = String(portfolio?.['Allocation Source'] || 'calculated').toLowerCase();
    const totalBalanceMw = allocationRows.reduce((sum, row) => sum + asNumber(balanceMwFromRow(row)), 0);
    const allocatedMwp = valueFrom(portfolio, ['Allocated MWP']);
    const oaUtilizationPct = totalBalanceMw > 0
      ? percentFromValues(allocatedMwp, totalBalanceMw)
      : valueFrom(portfolio, ['OA Utilization %']);
    const lapsedBreakdown = lapsedBreakdownFromPortfolio(portfolio);
    const postBankingLapsedPct = percentFromValues(
      lapsedBreakdown.unitLapsed,
      valueFrom(portfolio, ['Total Generation'])
    ) || valueFrom(portfolio, ['Post Banking Lapsed %']);
    const unallocatedMw = asNumber(valueFrom(portfolio, ['Unallocated MWP']));
    const kpis = [
      { title: 'Evacuation capacity', value: `${formatIndian(valueFrom(portfolio, ['Evacuation MW', 'Total MWP']))} MW` },
      { title: 'Allocated MW', value: formatIndian(valueFrom(portfolio, ['Allocated MWP'])) },
      { title: 'Unallocated MW', value: formatIndian(unallocatedMw) },
      { title: 'Post Banking Replacement', value: formatPercent(valueFrom(portfolio, ['Post Banking Replacement %', 'Portfolio Replacement %'])) },
      { title: 'Banking Settlement', value: formatIndian0(valueFrom(portfolio, ['Total Banking settlement', 'Banking Settlement'])) },
    ];

    return (
      <>
        <div id="allocation-banking-highlights" className="summary-section span-2 allocation-summary-section">
          <h3 className="section-title">Allocation & Banking Summary</h3>
          <div className="allocation-explainer">
            {allocationSource === 'manual' ? (
              <span>Capacity was allocated using the manual percentages saved in Multi-Unit OA Setup, and no unit exceeds its permissible headroom. </span>
            ) : (
              <span>Allocation is limited to evacuation capacity, and each unit stays within its permissible headroom. </span>
            )}
            {unallocatedMw > 0 ? (
              <span>Energy inside that evacuation limit that no unit could take is unallocated lapsed.</span>
            ) : (
              <span>Unit headroom covered the full evacuation capacity, so there is no unallocated lapsed energy.</span>
            )}
          </div>
          <div className="cards-grid allocation-kpi-grid">
            {kpis.map((card) => (
              <div key={card.title} className="summary-card">
                <div className="card-title">{card.title}</div>
                <div className="card-value">{card.value}</div>
              </div>
            ))}
          </div>
          <div className="allocation-lapsed-panel">
            <h4>Lapsed energy</h4>
            <p className="allocation-lapsed-intro">
              Unit lapsed is generation assigned to a unit and not consumed. Unallocated lapsed is evacuation energy that stayed unassigned because unit limits were already full.
            </p>
            <div className="allocation-lapsed-grid">
              <div className="allocation-lapsed-card">
                <div className="card-title">Unit lapsed</div>
                <div className="card-value">{formatIndian0(lapsedBreakdown.unitLapsed)}</div>
                <div className="allocation-lapsed-note">Sum of each unit’s unused allocated generation, after banking when banking has run.</div>
              </div>
              <div className="allocation-lapsed-card is-unallocated">
                <div className="card-title">Unallocated lapsed</div>
                <div className="card-value">{formatIndian0(lapsedBreakdown.unallocatedLapsed)}</div>
                <div className="allocation-lapsed-note">
                  {formatIndian(unallocatedMw)} MW of evacuation capacity could not be placed on any unit. That share of evacuated energy lapses and stays outside the unit rows.
                </div>
              </div>
              <div className="allocation-lapsed-card">
                <div className="card-title">Total lapsed</div>
                <div className="card-value">{formatIndian0(lapsedBreakdown.totalLapsed)}</div>
                <div className="allocation-lapsed-note">Unit lapsed plus unallocated lapsed.</div>
              </div>
            </div>
          </div>
        </div>

        <div className="summary-section allocation-summary-section">
          <h3 className="section-title">Efficiency Indicators</h3>
          <div className="allocation-progress-grid">
            {renderProgressMetric({
              label: 'Allocation efficiency',
              value: valueFrom(portfolio, ['Allocation Efficiency %']),
              hint: 'Allocated MW as a share of evacuation capacity',
            })}
            {renderProgressMetric({
              label: 'OA utilization',
              value: oaUtilizationPct,
          hint: 'Allocated MWP as a share of total available OA balance in MW',
            })}
            {renderProgressMetric({
              label: 'Post-banking replacement',
              value: valueFrom(portfolio, ['Post Banking Replacement %', 'Portfolio Replacement %']),
              hint: 'Demand met after banking as a share of consumption',
            })}
            {renderProgressMetric({
              label: 'Post-banking unit lapsed',
              value: postBankingLapsedPct,
              hint: 'Allocated generation that lapsed after banking, as a share of allocated generation. Unallocated lapsed is separate.',
            })}
          </div>
        </div>

        <div className="summary-section allocation-summary-section">
          <h3 className="section-title">Allocated MW vs OA Balance</h3>
          <div className="summary-chart allocation-bar-chart">
            {barData ? <Bar data={barData} options={multiUnitBarOptions} /> : <div className="summary-empty">No allocation rows available</div>}
          </div>
        </div>

        <div className="summary-section span-2 allocation-summary-section">
          <h3 className="section-title">Post-Banking Unit Performance</h3>
          {unitRows.length ? (
            <div className="summary-table-wrapper">
              <table className="summary-table allocation-unit-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Allocated MW</th>
                    <th>Balance MW</th>
                    <th>Balance Utilization</th>
                    <th>Replacement</th>
                    <th>Unmet</th>
                    <th>Lapsed</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {unitRows.map((row, idx) => {
                    const band = getEfficiencyBand(row);
                    const allocatedMw = valueFrom(row, ['Final Allocated MW']);
                    const balanceMw = balanceMwFromRow(row);
                    const balanceUtilization = percentFromValues(allocatedMw, balanceMw);
                    const replacementPct = valueFrom(row, ['Post Banking Replacement %', 'Replacement %']);
                    const unmet = valueFrom(row, ['Leftover Demand']);
                    const unmetPct = valueFrom(row, ['Unmet %']);
                    const lapsed = valueFrom(row, ['Lapsed']);
                    const lapsedPct = lapsedPercentFromRow(row);
                    return (
                      <tr key={`${row?.['Unit ID'] || row?.['Unit Name'] || 'unit'}-${idx}`}>
                        <td>{row?.['Unit Name'] || row?.['Unit ID'] || '-'}</td>
                        <td className="numeric-cell">{formatIndian(allocatedMw)}</td>
                        <td className="numeric-cell">{formatIndian(balanceMw)}</td>
                        <td className="numeric-cell">{formatPercent(balanceUtilization)}</td>
                        <td className="numeric-cell">{valueWithPercent(valueFrom(row, ['Demand Met']), replacementPct)}</td>
                        <td className="numeric-cell">{valueWithPercent(unmet, unmetPct)}</td>
                        <td className="numeric-cell">{valueWithPercent(lapsed, lapsedPct)}</td>
                        <td><span className={`allocation-status-chip ${getBandClass(band)}`}>{band}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="summary-empty">Run the Banking node to see post-banking unit performance.</div>
          )}
        </div>
      </>
    );
  };

  const handleRunPipeline = async () => {
    const effectivePipelineName = session.pipeline_name || pipelineName;
    if (!effectivePipelineName || effectivePipelineName === "Unsaved_Pipeline") {
      notifyError(new Error("Please create or open a pipeline before running."), {
        operation: OP.pipeline.runByName,
        toastId: 'pipeline.run.unsaved',
      });
      return;
    }

    setIsRunning(true);
    try {
      const result = await runPipelineByName(effectivePipelineName);
      if (result) {
        // Extract node names from Node_output_cell_ranges if it exists
        const runNodes = result.Node_output_cell_ranges
          ? Object.keys(result.Node_output_cell_ranges)
          : [];

        console.log('Nodes that were run:', runNodes);
        run_pipeline("success", runNodes, result.run_id || result.id || null);
      } else {
        run_pipeline("failure", [], null);
      }
      console.log('Pipeline run result:', result);
    } catch (error) {
      console.error('Pipeline run failed:', error);
      run_pipeline("failure", [], null);
      notifyError(error, { operation: OP.pipeline.runByName });
    } finally {
      setIsRunning(false);
    }
  };

  const handleCreatePipeline = async () => {
    try {
      const data = await createNewPipeline(newPipelineName);
      setCreationMessage(`Pipeline created successfully with ID: ${data.id}`);
      setIsError(false);
      const createdPipeline = {
        id: data.pipeline_id || data.id,
        pipeline_id: data.pipeline_id || data.id,
        pipeline_name: newPipelineName,
        owner_email: currentUser?.email,
        owner_user_id: currentUser?.user_id,
        access: 'owner',
      };
      
      // First reset to Unsaved_Pipeline to trigger cleanup
      clearActivePipelineContext();
      initialize_pipeline("Unsaved_Pipeline");
      setActivePipelineContext(createdPipeline);
      
      // Then set the new pipeline name after a short delay
      // This ensures the cleanup is complete before loading the new pipeline
      setTimeout(() => {
        setPipelineName(newPipelineName); // Update the pipeline name in the header
        setIsCreateModalOpen(false); // Close the modal
        initialize_pipeline(newPipelineName, createdPipeline);
        
        toast.success(`Pipeline '${newPipelineName}' created successfully!`, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      }, 100);
    } catch (error) {
      setCreationMessage(`Error: ${error.message}`);
      setIsError(true);
    }
  };

  const handleOpenPipeline = async () => {
    try {
      const data = await listPipelines();
      const pipelineItems = data.pipeline_items || (data.pipelines || []).map((name) => ({ pipeline_name: name }));
      setPipelines(pipelineItems);
      setPipelineSearch('');
      setSelectedPipeline(null);
      setIsOpenModalOpen(true);
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    }
  };

  const handleSelectPipeline = (pipeline) => {
    setSelectedPipeline(pipeline);
  };

  const handleOpenSelectedPipeline = async () => {
    if (!selectedPipeline) return;
    setIsOpenModalOpen(false);
    const pipelineMeta = typeof selectedPipeline === 'string'
      ? { pipeline_name: selectedPipeline }
      : selectedPipeline;
    const selectedName = pipelineMeta.pipeline_name;
    
    try {
      // First reset to Unsaved_Pipeline to trigger cleanup
      clearActivePipelineContext();
      initialize_pipeline("Unsaved_Pipeline");
      setActivePipelineContext(pipelineMeta);
      
      // Then load the selected pipeline after a short delay
      setTimeout(async () => {
        setPipelineName(selectedName);
        
        const data = await getPipelineByName(selectedName, { trackOpen: true, pipelineId: pipelineMeta.pipeline_id || pipelineMeta.id });
        if (data) {
          const openedPipeline = {
            ...pipelineMeta,
            ...data,
            id: data.pipeline_id || data.id || data._id || pipelineMeta.id,
            pipeline_id: data.pipeline_id || data.id || data._id || pipelineMeta.pipeline_id,
          };
          setActivePipelineContext(openedPipeline);
          // Initialize the pipeline context with the pipeline name
          initialize_pipeline(selectedName, openedPipeline);
          
          toast.success(`Pipeline '${selectedName}' opened successfully!`, {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
          
          // The Canvas component will handle loading the nodes based on the pipeline name
        }
      }, 100);
    } catch (error) {
      console.error('Error opening pipeline:', error);
      toast.error(`Failed to open pipeline: ${error.message}`, {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const handleOpenCreateModal = () => {
    setNewPipelineName(''); // Clear the text box value
    setCreationMessage(''); // Clear the message
    setIsError(false); // Reset error state
    setIsCreateModalOpen(true); // Open the modal
  };
  
  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };
  
  const handleOpenDeleteModal = () => {
    if (session.pipeline_name !== 'Unsaved_Pipeline') {
      setIsDeleteModalOpen(true);
    } else {
      toast.warning('Cannot delete an unsaved pipeline', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const handleOpenCopyModal = () => {
    if (session.pipeline_name && session.pipeline_name !== 'Unsaved_Pipeline') {
      setCopyPipelineName(`${session.pipeline_name}_copy`);
      setCopyMessage('');
      setCopyIsError(false);
      setIsCopyModalOpen(true);
    } else {
      toast.warning('Open a saved pipeline to copy', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const closeOpenModal = () => {
    setIsOpenModalOpen(false);
    setPipelineSearch('');
    setSelectedPipeline(null);
  };

  const handlePipelineSearchChange = (event) => {
    const value = event.target.value;
    setPipelineSearch(value);
    setSelectedPipeline((current) => selectionAfterSearch(pipelines, current, value));
  };

  const loadPipelineShares = async (pipelineId) => {
    if (!pipelineId) {
      setPipelineShares([]);
      return;
    }
    try {
      const data = await listPipelineShares(pipelineId);
      setPipelineShares(data?.shares || []);
    } catch (error) {
      setPipelineShares([]);
    }
  };

  const handleOpenShareModal = async () => {
    if (!session.pipeline_name || session.pipeline_name === 'Unsaved_Pipeline') {
      toast.warning('Open a saved pipeline to share', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }
    if (session.access === 'collaborator') return;
    const pipelineId = session.pipeline_id;
    if (!pipelineId) {
      toast.warning('Reopen this pipeline before sharing it', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }
    setShareEmail('');
    setShareMode('collaborate');
    setShareMessage('');
    setShareIsError(false);
    setIsShareModalOpen(true);
    await loadPipelineShares(pipelineId);
  };

  const handleSharePipeline = async () => {
    const pipelineId = session.pipeline_id;
    const email = (shareEmail || '').trim();
    if (!pipelineId) {
      setShareMessage('Reopen this pipeline before sharing it');
      setShareIsError(true);
      return;
    }
    if (!email) {
      setShareMessage('Enter the recipient email');
      setShareIsError(true);
      return;
    }
    setShareSubmitting(true);
    try {
      const result = await sharePipeline(pipelineId, email, shareMode);
      if (shareMode === 'replicate') {
        setShareMessage(`Copied as '${result?.pipeline_name || session.pipeline_name}' in ${result?.grantee_email || email}'s account`);
      } else {
        setShareMessage(`Shared with ${result?.grantee_email || email}`);
        await loadPipelineShares(pipelineId);
      }
      setShareIsError(false);
      setShareEmail('');
    } catch (error) {
      setShareMessage(error.message || 'Could not share this pipeline');
      setShareIsError(true);
    } finally {
      setShareSubmitting(false);
    }
  };

  const handleRevokeShare = async (userId) => {
    const pipelineId = session.pipeline_id;
    if (!pipelineId || !userId) return;
    try {
      await revokePipelineShare(pipelineId, userId);
      await loadPipelineShares(pipelineId);
      setShareMessage('Access removed');
      setShareIsError(false);
    } catch (error) {
      setShareMessage(error.message || 'Could not remove access');
      setShareIsError(true);
    }
  };

  const handleCopyPipeline = async () => {
    try {
      const source = session.pipeline_name;
      if (!source || source === 'Unsaved_Pipeline') {
        setCopyMessage('No saved pipeline to copy');
        setCopyIsError(true);
        return;
      }
      const target = (copyPipelineName || '').trim();
      if (!target) {
        setCopyMessage('Enter a name for the new pipeline');
        setCopyIsError(true);
        return;
      }
      const copied = await copyPipeline(source, target);
      const copiedPipeline = {
        id: copied?.pipeline_id || copied?.id,
        pipeline_id: copied?.pipeline_id || copied?.id,
        pipeline_name: target,
        owner_email: currentUser?.email,
        owner_user_id: currentUser?.user_id,
        access: 'owner',
      };
      // Reset to trigger cleanup, then open the new pipeline
      clearActivePipelineContext();
      initialize_pipeline('Unsaved_Pipeline');
      setTimeout(() => {
        setPipelineName(target);
        setIsCopyModalOpen(false);
        setActivePipelineContext(copiedPipeline);
        initialize_pipeline(target, copiedPipeline);
        toast.success(`Opened copied pipeline '${target}'`, {
          position: 'bottom-right', autoClose: 3000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true, theme: 'colored', transition: Bounce,
        });
      }, 100);
    } catch (error) {
      setCopyMessage(`Error: ${error.message}`);
      setCopyIsError(true);
    }
  };
  
  const handleDeletePipeline = async () => {
    try {
      await deletePipeline(session.pipeline_name);
      
      // Close the modal first
      setIsDeleteModalOpen(false);
      
      // Reset to Unsaved_Pipeline to clear the canvas
      // This will trigger the useEffect in Canvas.js that clears the nodes
      clearActivePipelineContext();
      initialize_pipeline("Unsaved_Pipeline");
      setPipelineName("Unsaved_Pipeline");
      
      // Add a success toast notification
      toast.success(`Pipeline deleted successfully`, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    } catch (error) {
      console.error('Error deleting pipeline:', error);
      // Error toast is already shown in the deletePipeline function
    }
  };

  const bankingSummaryUnitOptions = useMemo(() => {
    const options = Array.isArray(bankingSummary?.unit_filter_options)
      ? bankingSummary.unit_filter_options
      : [];
    return options
      .filter((option) => option && option.value != null)
      .map((option) => ({
        value: String(option.value),
        label: option.label || String(option.value),
      }));
  }, [bankingSummary]);

  const hasBankingSummaryUnitFilter = bankingSummaryUnitOptions.length > 1;

  useEffect(() => {
    if (!hasBankingSummaryUnitFilter) {
      if (selectedBankingSummaryUnit !== TOTAL_BANKING_UNIT_VALUE) {
        setSelectedBankingSummaryUnit(TOTAL_BANKING_UNIT_VALUE);
      }
      return;
    }
    const hasSelected = bankingSummaryUnitOptions.some((option) => option.value === selectedBankingSummaryUnit);
    if (!hasSelected) {
      setSelectedBankingSummaryUnit(TOTAL_BANKING_UNIT_VALUE);
    }
  }, [bankingSummaryUnitOptions, hasBankingSummaryUnitFilter, selectedBankingSummaryUnit]);

  const displayedProposedBankingSummary = useMemo(() => {
    if (!hasBankingSummaryUnitFilter) return bankingSummary;
    const summaries = bankingSummary?.unit_summaries || {};
    const selectedSummary = summaries[selectedBankingSummaryUnit] || summaries[TOTAL_BANKING_UNIT_VALUE];
    if (!selectedSummary) return bankingSummary;
    return {
      ...bankingSummary,
      ...selectedSummary,
      selected_unit_id: selectedBankingSummaryUnit,
      unit_filter_options: bankingSummary.unit_filter_options,
      unit_summaries: bankingSummary.unit_summaries,
    };
  }, [bankingSummary, hasBankingSummaryUnitFilter, selectedBankingSummaryUnit]);

  // Existing OA / Proposed / Total round selector for the TOD table, the
  // monthly "RE vs Consumption" table, and the annual summary cards. Hidden
  // entirely (falls back to today's proposal-only view) unless the Existing
  // OA banking round was actually run for this pipeline/run.
  const hasBankingRoundFilter = existingOaBankingSummary?.available === true;

  const bankingRoundOptions = useMemo(() => ([
    { value: BANKING_ROUND_PROPOSED, label: 'Proposed' },
    { value: BANKING_ROUND_EXISTING_OA, label: 'Existing OA' },
    { value: BANKING_ROUND_TOTAL, label: 'Total' },
  ]), []);

  useEffect(() => {
    if (!hasBankingRoundFilter && selectedBankingRound !== BANKING_ROUND_PROPOSED) {
      setSelectedBankingRound(BANKING_ROUND_PROPOSED);
    }
  }, [hasBankingRoundFilter, selectedBankingRound]);

  // Existing OA and the proposal can, in principle, settle under different
  // banking policies with different Slot definitions, which would make a
  // per-slot Total ill-defined. We assume they use the same policy (the
  // common case) and combine at Slot grain too; only when the two policies
  // actually differ do we refuse the per-slot Total and surface that as an
  // error on the tab instead of silently combining mismatched slots.
  const bankingPolicyMismatch = hasBankingRoundFilter
    && bankingPolicyNames.proposed.length > 0
    && bankingPolicyNames.existingOa.length > 0
    && (
      bankingPolicyNames.proposed.length !== bankingPolicyNames.existingOa.length
      || bankingPolicyNames.proposed.some((name, idx) => name !== bankingPolicyNames.existingOa[idx])
    );

  // The Existing OA round and the proposal round are NOT two independent
  // consumption pools to add together: Existing OA settles against the full
  // original consumption first, and whatever it leaves unmet ("Balance
  // consumption for proposal") is what the proposal round's own analysis
  // treats as its Consumption. So:
  //  - Consumption is the Existing OA round's figure (the superset) — never
  //    summed with the proposal round's, which is only the OA leftover.
  //  - DemandMet is additive: it's satisfied by two distinct mechanisms (OA
  //    banking, then proposal RE/banking) against that same total.
  //  - LapsedUnits is the proposal round's own figure (the final unmet
  //    demand after both rounds) — Existing OA's "lapsed" is not final, it's
  //    exactly the amount that becomes the proposal round's Consumption.
  const combineBankingRows = (proposedRows, oaRows, keyField) => {
    const oaByKey = new Map();
    oaRows.forEach((row) => {
      const key = String(row?.[keyField] ?? '');
      if (key && key !== 'Total') oaByKey.set(key, row);
    });
    const proposedByKey = new Map();
    proposedRows.forEach((row) => {
      const key = String(row?.[keyField] ?? '');
      if (key && key !== 'Total') proposedByKey.set(key, row);
    });
    const keys = new Set([...oaByKey.keys(), ...proposedByKey.keys()]);
    return Array.from(keys).map((key) => {
      const oaRow = oaByKey.get(key);
      const proposedRow = proposedByKey.get(key);
      const consumption = oaRow ? (Number(oaRow.Consumption) || 0) : (Number(proposedRow?.Consumption) || 0);
      const demandMet = (Number(oaRow?.DemandMet) || 0) + (Number(proposedRow?.DemandMet) || 0);
      const lapsedUnits = proposedRow ? (Number(proposedRow.LapsedUnits) || 0) : (Number(oaRow?.LapsedUnits) || 0);
      return {
        key,
        sourceRow: proposedRow || oaRow,
        Consumption: consumption,
        DemandMet: demandMet,
        LapsedUnits: lapsedUnits,
        SatisfiedPct: consumption > 0 ? (demandMet / consumption) * 100 : 0,
        LapsedPct: consumption > 0 ? (lapsedUnits / consumption) * 100 : 0,
      };
    });
  };

  const combinedTotalBankingSummary = useMemo(() => {
    const proposedRows = getMonthlyBankingRows(displayedProposedBankingSummary);
    const oaRows = getMonthlyBankingRows(existingOaBankingSummary);
    const rows = combineBankingRows(proposedRows, oaRows, 'MonthName')
      .map(({ key, sourceRow, ...totals }) => ({
        Month: sourceRow?.Month,
        MonthName: sourceRow?.MonthName ?? key,
        ...totals,
      }))
      .sort((a, b) => Number(a.Month) - Number(b.Month));
    const totalRow = rows.reduce((acc, row) => ({
      Consumption: acc.Consumption + row.Consumption,
      DemandMet: acc.DemandMet + row.DemandMet,
      LapsedUnits: acc.LapsedUnits + row.LapsedUnits,
    }), { Consumption: 0, DemandMet: 0, LapsedUnits: 0 });
    rows.push({
      Month: 0,
      MonthName: 'Total',
      ...totalRow,
      SatisfiedPct: totalRow.Consumption > 0 ? (totalRow.DemandMet / totalRow.Consumption) * 100 : 0,
      LapsedPct: totalRow.Consumption > 0 ? (totalRow.LapsedUnits / totalRow.Consumption) * 100 : 0,
    });

    let todRows = [];
    let todApplicable = true;
    let todMessage = '';
    if (bankingPolicyMismatch) {
      todApplicable = false;
      todMessage = `Combined TOD totals cannot be shown: Existing OA is settling under banking polic${bankingPolicyNames.existingOa.length > 1 ? 'ies' : 'y'} "${bankingPolicyNames.existingOa.join('", "')}" while the proposal uses "${bankingPolicyNames.proposed.join('", "')}". Select the same banking policy for both to see a combined TOD total.`;
    } else {
      const proposedTodRows = getTodBankingRows(displayedProposedBankingSummary);
      const oaTodRows = getTodBankingRows(existingOaBankingSummary);
      todRows = combineBankingRows(proposedTodRows, oaTodRows, 'Slot')
        .map(({ key, sourceRow, ...totals }) => ({
          Slot: sourceRow?.Slot ?? key,
          ...totals,
        }));
      if (todRows.length) {
        const todTotal = todRows.reduce((acc, row) => ({
          Consumption: acc.Consumption + row.Consumption,
          DemandMet: acc.DemandMet + row.DemandMet,
          LapsedUnits: acc.LapsedUnits + row.LapsedUnits,
        }), { Consumption: 0, DemandMet: 0, LapsedUnits: 0 });
        todRows.push({
          Slot: 'Total',
          ...todTotal,
          SatisfiedPct: todTotal.Consumption > 0 ? (todTotal.DemandMet / todTotal.Consumption) * 100 : 0,
          LapsedPct: todTotal.Consumption > 0 ? (todTotal.LapsedUnits / todTotal.Consumption) * 100 : 0,
        });
      }
    }

    return {
      tod_summary: todRows,
      tod_applicable: todApplicable,
      tod_message: todMessage,
      monthly_summary: rows,
      monthly_summary_mode: 'combined_total',
    };
  }, [displayedProposedBankingSummary, existingOaBankingSummary, bankingPolicyMismatch, bankingPolicyNames]);

  const displayedBankingSummary = useMemo(() => {
    if (selectedBankingRound === BANKING_ROUND_EXISTING_OA) return existingOaBankingSummary;
    if (selectedBankingRound === BANKING_ROUND_TOTAL) return combinedTotalBankingSummary;
    return displayedProposedBankingSummary;
  }, [selectedBankingRound, existingOaBankingSummary, combinedTotalBankingSummary, displayedProposedBankingSummary]);

  const renderBankingSummaryRoundFilter = () => {
    if (!hasBankingRoundFilter) return null;
    return (
      <label className="summary-unit-filter">
        <span>Round</span>
        <select
          value={selectedBankingRound}
          onChange={(event) => setSelectedBankingRound(event.target.value)}
        >
          {bankingRoundOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
    );
  };

  const renderBankingSummaryUnitFilter = () => {
    // The unit filter only applies to the proposal round's own per-unit data;
    // Existing OA / Total views are single-unit for v1.
    if (selectedBankingRound !== BANKING_ROUND_PROPOSED) return null;
    if (!hasBankingSummaryUnitFilter) return null;
    return (
      <label className="summary-unit-filter">
        <span>Unit</span>
        <select
          value={selectedBankingSummaryUnit}
          onChange={(event) => setSelectedBankingSummaryUnit(event.target.value)}
        >
          {bankingSummaryUnitOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
    );
  };

  const monthlyBankingColumns = getMonthlyBankingColumns(displayedBankingSummary);
  const monthlyBankingRows = getMonthlyBankingRows(displayedBankingSummary);
  const isAdmin = currentUser?.role === 'admin';
  const canManagePipeline = session.pipeline_name !== 'Unsaved_Pipeline' && session.access !== 'collaborator';
  const visiblePipelines = filterPipelines(pipelines, pipelineSearch);

  return (
    <header className="header">
      <div className="header-info">
        <h2>Integrum Pipeline Tool</h2>
        <div className="pipeline-name-container">
          <div className="pipeline-name-display">
            {session.pipeline_name}
            {session.access === 'collaborator' && session.owner_email && session.pipeline_name !== 'Unsaved_Pipeline' && (
              <small className="pipeline-owner-label">Shared by {session.owner_email}</small>
            )}
            {isAdmin && session.access !== 'collaborator' && session.owner_email && session.pipeline_name !== 'Unsaved_Pipeline' && (
              <small className="pipeline-owner-label">Owner: {session.owner_email}</small>
            )}
          </div>
          {canManagePipeline && (
            <FaTrash 
              className="delete-icon" 
              onClick={handleOpenDeleteModal} 
              title="Delete Pipeline"
            />
          )}
        </div>
      </div>
      <div className="header-controls">
        {activePage !== 'workspace' && (
          <button className="header-button" onClick={onOpenWorkspace} title="Back to workspace">
            Workspace
          </button>
        )}
        <button className="header-button" onClick={handleOpenCopyModal} title="Copy Pipeline">
          <MdContentCopy className="icon" /> Copy
        </button>
        {canManagePipeline && (
          <button className="header-button" onClick={handleOpenShareModal} title="Share Pipeline">
            <MdShare className="icon" /> Share
          </button>
        )}
        <button className="header-button" onClick={openSummary} title="Project Summary">
          <MdDashboard className="icon" /> Summary
        </button>
        <button className="header-button" onClick={handleOpenCreateModal}>
          <MdCreateNewFolder className="icon" /> New
        </button>
        <button className="header-button" onClick={handleOpenPipeline}>
          <MdOpenInNew className="icon" /> Open
        </button>
        <button className="header-button" onClick={handleOpenUploadModal}>
          <MdOutlineFileUpload className="icon" /> Upload
        </button>
        <button className="header-button" onClick={() => setIsSetupOpen(true)} title="Setup Utility">
          <MdTune className="icon" /> Setup
        </button>
        <button className={`header-button ${activePage === 'scenarioAnalysis' ? 'active' : ''}`} onClick={onOpenScenarioAnalysis} title="Scenario optimization">
          <MdGridView className="icon" /> Scenarios
        </button>
        <button className={`header-button ${activePage === 'multiUnitOptimization' ? 'active' : ''}`} onClick={onOpenMultiUnitOptimization} title="Multi Unit optimization">
          <MdBalance className="icon" /> Reallocate
        </button>
        {isAdmin && (
          <button className={`header-button admin-ops-button ${activePage === 'adminOps' ? 'active' : ''}`} onClick={onOpenAdminOps} title="Admin usage and tracking">
            <MdAdminPanelSettings className="icon" /> Ops
          </button>
        )}
        <button
          className={`header-button run-button ${isRunning ? 'stop' : ''}`}
          onClick={handleRunPipeline}
        >
          {isRunning ? <FaStop className="icon" /> : <FaPlay className="icon" />} Run
        </button>
        <div className="account-menu" title={currentUser?.email || 'Signed in'}>
          <div className="account-summary">
            <FaUserCircle className="account-icon" />
            <div className="account-text">
              <span className="account-label">Account</span>
              <span className="account-email">{currentUser?.email || 'Signed in'}</span>
            </div>
          </div>
          <button className="signout-button" onClick={onSignOut} type="button">
            <FaSignOutAlt className="icon" /> Sign out
          </button>
        </div>
      </div>

      {/* Create Pipeline Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onRequestClose={() => setIsCreateModalOpen(false)}
        contentLabel="Create New Pipeline"
        className="modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <IoClose className="close-icon" onClick={() => setIsCreateModalOpen(false)} />
        </div>
        <div className="modal-content">
          <h2>Create New Pipeline</h2>
          <input
            type="text"
            placeholder="Enter pipeline name"
            value={newPipelineName}
            onChange={(e) => setNewPipelineName(e.target.value)}
            className="modal-input"
          />
          <button onClick={handleCreatePipeline} className="modal-button">Create</button>
          {creationMessage && (
            <p className={isError ? 'error-message' : 'success-message'}>
              {creationMessage}
            </p>
          )}
        </div>
      </Modal>

      {/* Open Pipeline Modal */}
      <Modal
        isOpen={isOpenModalOpen}
        onRequestClose={closeOpenModal}
        contentLabel="Open Pipeline"
        className="modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <IoClose className="close-icon" onClick={closeOpenModal} />
        </div>
        <div className="modal-content">
          <h2>Select a Pipeline to Open</h2>
          <input
            type="search"
            placeholder="Search by name or owner email"
            value={pipelineSearch}
            onChange={handlePipelineSearchChange}
            className="modal-input"
          />
          <div className="pipeline-list">
            {visiblePipelines.length === 0 ? (
              <div className="pipeline-empty">
                {pipelineSearch.trim() ? 'No pipelines match your search.' : 'No pipelines yet.'}
              </div>
            ) : visiblePipelines.map((pipeline, index) => {
              const item = typeof pipeline === 'string' ? { pipeline_name: pipeline } : pipeline;
              const itemId = pipelineItemIdentity(item);
              const selectedId = selectedPipeline ? pipelineItemIdentity(selectedPipeline) : null;
              return (
                <div
                  key={itemId || index}
                  className={`pipeline-item ${selectedId === itemId ? 'selected' : ''}`}
                  onClick={() => handleSelectPipeline(item)}
                >
                  <span>{item.pipeline_name}</span>
                  {item.access === 'collaborator' && item.owner_email && (
                    <small className="pipeline-owner-label">Shared by {item.owner_email}</small>
                  )}
                  {isAdmin && item.access !== 'collaborator' && item.owner_email && (
                    <small className="pipeline-owner-label">{item.owner_email}</small>
                  )}
                </div>
              );
            })}
          </div>
          <button onClick={handleOpenSelectedPipeline} className="modal-button" disabled={!selectedPipeline}>Open</button>
        </div>
      </Modal>

      <Modal
        isOpen={isShareModalOpen}
        onRequestClose={() => setIsShareModalOpen(false)}
        contentLabel="Share Pipeline"
        className="modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <IoClose className="close-icon" onClick={() => setIsShareModalOpen(false)} />
        </div>
        <div className="modal-content">
          <h2>Share Pipeline</h2>
          <div className="modal-hint">Source: {session.pipeline_name || '-'}</div>
          <input
            type="email"
            placeholder="name@integrumenergy.in"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            className="modal-input"
          />
          <div className="share-mode-row">
            <button
              type="button"
              className={`modal-button ${shareMode === 'collaborate' ? '' : 'cancel-button'}`}
              onClick={() => setShareMode('collaborate')}
            >
              Collaborate
            </button>
            <button
              type="button"
              className={`modal-button ${shareMode === 'replicate' ? '' : 'cancel-button'}`}
              onClick={() => setShareMode('replicate')}
            >
              Replicate
            </button>
          </div>
          <p className="modal-hint">
            {shareMode === 'collaborate'
              ? 'Collaborate gives them the same pipeline. They can edit it, and each person keeps their own runs.'
              : 'Replicate copies this pipeline into their account. The copy is independent.'}
          </p>
          <button onClick={handleSharePipeline} className="modal-button" disabled={shareSubmitting}>
            {shareSubmitting ? 'Sharing...' : 'Share'}
          </button>
          {shareMessage && (
            <p className={shareIsError ? 'error-message' : 'success-message'}>
              {shareMessage}
            </p>
          )}
          {pipelineShares.length > 0 && (
            <div className="pipeline-list share-list">
              {pipelineShares.map((share) => (
                <div className="pipeline-item" key={share.user_id || share.email}>
                  <span>{share.email}</span>
                  <button type="button" className="share-revoke" onClick={() => handleRevokeShare(share.user_id)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Upload Data File Modal */}
      <UploadModal 
        isOpen={isUploadModalOpen}
        onRequestClose={() => setIsUploadModalOpen(false)}
      />

      {/* Copy Pipeline Modal */}
      <Modal
        isOpen={isCopyModalOpen}
        onRequestClose={() => setIsCopyModalOpen(false)}
        contentLabel="Copy Pipeline"
        className="modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <IoClose className="close-icon" onClick={() => setIsCopyModalOpen(false)} />
        </div>
        <div className="modal-content">
          <h2>Copy Pipeline</h2>
          <div className="modal-hint">Source: {session.pipeline_name || '-'}</div>
          <input
            type="text"
            placeholder="Enter new pipeline name"
            value={copyPipelineName}
            onChange={(e) => setCopyPipelineName(e.target.value)}
            className="modal-input"
          />
          <button onClick={handleCopyPipeline} className="modal-button">Copy</button>
          {copyMessage && (
            <p className={copyIsError ? 'error-message' : 'success-message'}>
              {copyMessage}
            </p>
          )}
        </div>
      </Modal>

      {/* Setup Utility Modal */}
      <SetupUtility 
        isOpen={isSetupOpen}
        onRequestClose={() => setIsSetupOpen(false)}
      />

      {/* Project Summary Modal */}
      <Modal
        isOpen={isSummaryOpen}
        onRequestClose={() => setIsSummaryOpen(false)}
        contentLabel="Project Summary"
        className="modal summary-modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <IoClose className="close-icon" onClick={() => setIsSummaryOpen(false)} />
        </div>
        <div className="modal-content summary-content">
          <div className="summary-header-row">
            <h2 className="summary-title">Project Summary</h2>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                className="summary-download-button"
                onClick={handleDownloadSummaryPdf}
                disabled={summaryLoading}
                title="Download Project PDF (Assumptions + Summary) (print to PDF)"
              >
                {summaryLoading ? 'Preparing...' : 'Download PDF'}
              </button>
            </div>
          </div>
          <div className="summary-tabs">
            <button className={`summary-tab ${activeSummaryTab==='overview' ? 'active' : ''}`} onClick={()=>setActiveSummaryTab('overview')}>Overview</button>
            <button className={`summary-tab ${activeSummaryTab==='allocation' ? 'active' : ''}`} onClick={()=>setActiveSummaryTab('allocation')}>Allocation & Banking</button>
            <button className={`summary-tab ${activeSummaryTab==='visualizations' ? 'active' : ''}`} onClick={()=>setActiveSummaryTab('visualizations')}>Visualizations</button>
            <button className={`summary-tab ${activeSummaryTab==='assumptions' ? 'active' : ''}`} onClick={()=>setActiveSummaryTab('assumptions')}>Project Assumptions</button>
            <button className={`summary-tab ${activeSummaryTab==='billing' ? 'active' : ''}`} onClick={()=>setActiveSummaryTab('billing')}>Billing Analytics</button>
            <button className={`summary-tab ${activeSummaryTab==='batteryTod' ? 'active' : ''}`} onClick={()=>setActiveSummaryTab('batteryTod')}>Battery TOD</button>
          </div>
          {(summaryError) && <div className="summary-error">{summaryError}</div>}
          {summaryLoading ? (
            <div className="summary-loading">Loading...</div>
          ) : (
            <div className="summary-grid">
              {activeSummaryTab === 'overview' && (
                <>
              {/* Finance Highlights Section */}
              <div id="finance-highlights" className="summary-section summary-finance-highlights">
                <h3 className="section-title">Finance Highlights</h3>
                <div className="cards-grid summary-finance-cards">
                  {getFinanceLastSevenCards().map((c, idx) => {
                    const slug = slugify(c.rawTitle);
                    return (
                      <div key={idx} className="summary-card finance-card" id={`finance-card-${slug}`}>
                        <div className="card-title" id={`finance-card-title-${slug}`}>{c.title}</div>
                        <div className="card-value" id={`finance-card-value-${slug}`}>{c.value}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Finance Chart Section */}
              <div id="finance-chart" className="summary-section summary-finance-chart">
                <h3 className="section-title">Total vs Discom Cost (in Cr)</h3>
                {getFinanceChartData() ? (
                  <div className="summary-chart finance-chart">
                    <Line 
                      data={getFinanceChartData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'top' },
                        },
                        scales: { 
                          x: { title: { display: true, text: 'Year' } }, 
                          y: { 
                            title: { display: true, text: 'Cost' },
                            ticks: { callback: (value) => Number(value).toFixed(2) }
                          } 
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="summary-empty">Run Finance node to see chart</div>
                )}
              </div>
              {/* Operational Costs Section */}
              <div id="operational-costs" className="summary-section summary-operational-costs span-2">
                <h3 className="section-title">Consumer Effective Levellised Cost (INR)</h3>
                <div className="cards-grid summary-operational-cards">
                  {getOperationalCards().map((c, idx) => {
                    const slug = slugify(c.rawTitle);
                    return (
                      <div key={idx} className="summary-card op-card" id={`op-card-${slug}`}>
                        <div className="card-title" id={`op-card-title-${slug}`}>{c.title}</div>
                        <div className="card-value" id={`op-card-value-${slug}`}>{c.value}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {renderBatterySummaryTable()}

              {/* TOD-wise banking summary table */}
              <div id="tod-banking-summary" className="summary-section span-2">
                <div className="section-title-row">
                  <h3 className="section-title">Banking summary by TOD</h3>
                  {renderBankingSummaryRoundFilter()}
                  {renderBankingSummaryUnitFilter()}
                </div>
                {(displayedBankingSummary?.tod_summary || []).length ? (
                  <div className="summary-table-wrapper">
                    <table className="summary-table">
                      <thead>
                        <tr>
                          <th>Time slots</th>
                          <th>Consumption</th>
                          <th>Demand met</th>
                          <th>% satisfied</th>
                          <th>Lapsed (%)</th>
                          <th>Lapsed units</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(displayedBankingSummary.tod_summary || []).map((row, idx) => (
                              <tr key={idx}>
                                <td>{String(row?.Slot || '').trim() || (displayedBankingSummary?.slot_time_labels?.[row.Slot] || '')}</td>
                                <td className="numeric-cell">{formatIndian0(row.Consumption)}</td>
                                <td className="numeric-cell">{formatIndian0(row.DemandMet)}</td>
                                <td className="numeric-cell">{formatPercent2(row.SatisfiedPct)}</td>
                                <td className="numeric-cell">{formatPercent2(row.LapsedPct)}</td>
                                <td className={`numeric-cell ${Number(row.LapsedUnits) < 0 ? 'negative' : ''}`}>
                                  {formatIndian0(row.LapsedUnits)}
                                </td>
                              </tr>
                            ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={selectedBankingRound === BANKING_ROUND_TOTAL && bankingPolicyMismatch ? 'summary-error' : 'summary-empty'}>
                    {displayedBankingSummary?.tod_applicable === false
                      ? (displayedBankingSummary?.tod_message || 'TOD summary is not applicable for this banking policy.')
                      : 'Run the Banking node to see the TOD summary'}
                  </div>
                )}
              </div>

              {/* Monthly banking summary table */}
              <div id="monthly-banking-summary" className="summary-section span-2">
                <div className="section-title-row">
                  <h3 className="section-title">RE power Vs Consumption Monthly Analysis</h3>
                  {renderBankingSummaryRoundFilter()}
                  {renderBankingSummaryUnitFilter()}
                </div>
                {monthlyBankingRows.length ? (
                  <div className="summary-table-wrapper">
                    <table className="summary-table">
                      <thead>
                        <tr>
                          {monthlyBankingColumns.map((col) => (
                            <th key={col.key}>{col.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyBankingRows.map((row, idx) => (
                          <tr key={idx}>
                            {monthlyBankingColumns.map((col) => {
                              const rawValue = row?.[col.key];
                              const isNumeric = col.type === 'number' || col.type === 'percent';
                              const isNegative = col.type === 'number' && Number(rawValue) < 0;
                              return (
                                <td
                                  key={col.key}
                                  className={`${isNumeric ? 'numeric-cell' : ''} ${isNegative ? 'negative' : ''}`.trim()}
                                >
                                  {formatBankingSummaryCell(rawValue, col.type)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="summary-empty">Run the Banking node to see the monthly summary</div>
                )}
                {displayedBankingSummary?.note ? (
                  <div className="summary-empty">{displayedBankingSummary.note}</div>
                ) : null}
              </div>

              {/* Annual supply and consumption summary */}
              <div id="annual-supply-consumption" className="summary-section span-2">
                <div className="section-title-row">
                  <h3 className="section-title">Annual supply and consumption summary</h3>
                  {renderBankingSummaryRoundFilter()}
                </div>
                {annualMetrics ? (
                  <div className="cards-grid">
                    <div className="summary-card"><div className="card-title">Total annual generation</div><div className="card-value">{formatIndian0(annualMetrics.totalAnnualGeneration)}</div></div>
                    <div className="summary-card"><div className="card-title">Total wind generation</div><div className="card-value">{formatIndian0(annualMetrics.totalWindGeneration)}</div></div>
                    <div className="summary-card"><div className="card-title">Total solar generation</div><div className="card-value">{formatIndian0(annualMetrics.totalSolarGeneration)}</div></div>
                    <div className="summary-card"><div className="card-title">Total curtailed units</div><div className="card-value">{formatIndian0(annualMetrics.totalCurtailedUnits)}</div></div>
                    <div className="summary-card"><div className="card-title">RE supplied units</div><div className="card-value">{formatIndian0(annualMetrics.reSuppliedUnits)}</div></div>
                    <div className="summary-card"><div className="card-title">Total annual consumption</div><div className="card-value">{formatIndian0(annualMetrics.originalAnnualConsumption)}</div></div>
                    <div className="summary-card"><div className="card-title">Balance consumption for proposal</div><div className="card-value">{formatIndian0(annualMetrics.balanceAnnualConsumption)}</div></div>
                    <div className="summary-card"><div className="card-title">Existing OA Solar/Wind</div><div className="card-value">{formatIndian0(annualMetrics.existingOaReUnits)} <small>({formatPercent2(annualMetrics.existingOaRePct)})</small></div></div>
                    <div className="summary-card"><div className="card-title">Existing Rooftop Solar (BTM)</div><div className="card-value">{formatIndian0(annualMetrics.existingBtmUnits)} <small>({formatPercent2(annualMetrics.existingBtmPct)})</small></div></div>
                    <div className="summary-card"><div className="card-title">Proposed RE replacement</div><div className="card-value">{formatIndian0(annualMetrics.proposedReUnits)} <small>({formatPercent2(annualMetrics.proposedRePct)})</small></div></div>
                    <div className="summary-card"><div className="card-title">Overall RE replacement</div><div className="card-value">{formatIndian0(annualMetrics.overallReUnits)} <small>({formatPercent2(annualMetrics.overallRePct)})</small></div></div>
                    <div className="summary-card"><div className="card-title">Hourly units banked</div><div className="card-value">{formatIndian0(annualMetrics.hourlyUnitsBanked)}</div></div>
                    <div className="summary-card"><div className="card-title">Lapsed units</div><div className="card-value">{formatIndian0(bankingCardFigures.lapsedUnits)}</div></div>
                    <div className="summary-card"><div className="card-title">Banked units used</div><div className="card-value">{formatIndian0(bankingCardFigures.bankedUnitsUsed)}</div></div>
                    <div className="summary-card"><div className="card-title">Net grid units</div><div className="card-value">{formatIndian0(bankingCardFigures.netGridUnits)}</div></div>
                    <div className="summary-card"><div className="card-title">Units from OA</div><div className="card-value">{formatIndian0(bankingCardFigures.unitsFromOA)}</div></div>
                    <div className="summary-card"><div className="card-title">Battery charging units</div><div className="card-value">{formatIndian0(annualMetrics.batteryChargingUnits)}</div></div>
                    <div className="summary-card"><div className="card-title">Battery discharging units</div><div className="card-value">{formatIndian0(annualMetrics.batteryDischargingUnits)}</div></div>
                  </div>
                ) : (
                  <div className="summary-empty">Run relevant nodes (Capper, Scaler, Loss, Consumption, Banking) to see this summary</div>
                )}
              </div>

              {/* Existing sources settled in priority order (Settle nodes) */}
              <SourceSettlementSection summary={sourceSettlementSummary} />

              {/* Donut charts comparisons */}
              <div id="annual-donuts" className="summary-section span-2">
                <h3 className="section-title">Comparisons</h3>
                <div className="charts-grid">
                  <div className="summary-chart" style={{ minHeight: 160 }}>
                    <div className="chart-title">Units from OA vs Total annual consumption</div>
                    <div className="chart-canvas">
                      {annualMetrics && getDonutData('Units from OA', annualMetrics.unitsFromOA, 'Total consumption', annualMetrics.totalAnnualConsumption, '#16a34a') ? (
                        <Doughnut data={getDonutData('Units from OA', annualMetrics.unitsFromOA, 'Total consumption', annualMetrics.totalAnnualConsumption, '#16a34a')} options={smallDonutOptions} />
                      ) : (
                        <div className="summary-empty">Insufficient data</div>
                      )}
                    </div>
                  </div>
                  <div className="summary-chart" style={{ minHeight: 160 }}>
                    <div className="chart-title">Total annual consumption vs Net grid units</div>
                    <div className="chart-canvas">
                      {annualMetrics && getDonutData('Net grid units', annualMetrics.netGridUnits, 'Total consumption', annualMetrics.totalAnnualConsumption, '#3b82f6') ? (
                        <Doughnut data={getDonutData('Net grid units', annualMetrics.netGridUnits, 'Total consumption', annualMetrics.totalAnnualConsumption, '#3b82f6')} options={smallDonutOptions} />
                      ) : (
                        <div className="summary-empty">Insufficient data</div>
                      )}
                    </div>
                  </div>
                  <div className="summary-chart" style={{ minHeight: 160 }}>
                    <div className="chart-title">Annual generation vs Lapsed units</div>
                    <div className="chart-canvas">
                      {annualMetrics && getDonutData('Lapsed units', annualMetrics.lapsedUnits, 'Annual generation', annualMetrics.totalAnnualGeneration, '#fb923c') ? (
                        <Doughnut data={getDonutData('Lapsed units', annualMetrics.lapsedUnits, 'Annual generation', annualMetrics.totalAnnualGeneration, '#fb923c')} options={smallDonutOptions} />
                      ) : (
                        <div className="summary-empty">Insufficient data</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </>
              )}

              {activeSummaryTab === 'allocation' && renderMultiUnitSummaryTab()}

              {activeSummaryTab === 'batteryTod' && renderBatteryTodTab()}

              {activeSummaryTab === 'assumptions' && (
                <div id="project-assumptions" className="summary-section span-2">
                  <h3 className="section-title">Project Assumptions</h3>
                  {renderAssumptionsTable()}
                </div>
              )}

              {activeSummaryTab === 'visualizations' && (
                <div id="viz-tab" className="summary-section span-2">
                  <h3 className="section-title">Consumption vs Demand met</h3>
                  {/* Slot-wise line chart */}
                  <div className="summary-section">
                    <div className="cards-grid" style={{gridTemplateColumns:'1fr'}}>
                      <div className="summary-card">
                        <div className="card-title">Slot-wise (Line)</div>
                        <select className="modal-input" value={selectedSlot} onChange={e=>setSelectedSlot(e.target.value)}>
                          <option value="">Select slot</option>
                          {getAllSlots().map((s,idx)=>(<option key={idx} value={s}>{s}</option>))}
                        </select>
                        <div className="summary-chart" style={{ minHeight: 220 }}>
                          <div className="chart-canvas">
                            {buildSlotLineData() ? (
                              <Line data={buildSlotLineData()} options={{ responsive:true, maintainAspectRatio:false, plugins:{ legend:{ position:'top' } }, scales:{ x:{ title:{ display:true, text:'Month'}}, y:{ title:{ display:true, text:'Units (kWh)'}}}}} />
                            ) : (
                              <div className="summary-empty">Select a slot to view chart</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Month-wise bar chart */}
                  <div className="summary-section">
                    <div className="cards-grid" style={{gridTemplateColumns:'1fr'}}>
                      <div className="summary-card">
                        <div className="card-title">All months (Bar) - per Slot</div>
                        <div className="summary-chart" style={{ minHeight: 260 }}>
                          <div className="chart-canvas">
                            {buildMonthBarData() ? (
                              <Bar data={buildMonthBarData()} options={{ responsive:true, maintainAspectRatio:false, plugins:{ legend:{ position:'top' } }, scales:{ x:{ title:{ display:true, text:'Month'}}, y:{ title:{ display:true, text:'Units (kWh)'}}}}} />
                            ) : (
                              <div className="summary-empty">No data available</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSummaryTab === 'billing' && (
                <div id="billing-analytics" className="summary-section span-2">
                  <div className="section-title-row">
                    <h3 className="section-title">Billing Analytics</h3>
                    <label className="summary-export-toggle">
                      <input
                        type="checkbox"
                        checked={includeBillingAnalyticsInPdf}
                        onChange={(e) => setIncludeBillingAnalyticsInPdf(e.target.checked)}
                      />
                      Include in PDF export
                    </label>
                  </div>
                  <BillingAnalyticsSection
                    globalParams={globalParams}
                    annualMetrics={annualMetrics}
                    financeInputs={financeInputs}
                    bankingSummary={bankingSummary}
                    opCostsOutput={opCostsOutput}
                    pipelineName={session.pipeline_name}
                    initialParams={billingAnalyticsParams}
                    initialParamsLoaded={billingAnalyticsParamsLoaded}
                    onParamsChange={handleBillingAnalyticsParamsChange}
                    includeOperationalCostInPdf={includeOperationalCostInPdf}
                    onIncludeOperationalCostInPdfChange={setIncludeOperationalCostInPdf}
                    includeTodWiseTariffInPdf={includeTodWiseTariffInPdf}
                    onIncludeTodWiseTariffInPdfChange={setIncludeTodWiseTariffInPdf}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Pipeline Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Pipeline Confirmation"
        className="modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <IoClose className="close-icon" onClick={() => setIsDeleteModalOpen(false)} />
        </div>
        <div className="modal-content">
          <h2>Delete Pipeline</h2>
          <p className="delete-confirmation-message">
            Are you sure you want to delete "{session.pipeline_name}"?
          </p>
          <div className="delete-modal-buttons">
            <button 
              onClick={handleDeletePipeline} 
              className="modal-button delete-button"
            >
              Yes, Delete
            </button>
            <button 
              onClick={() => setIsDeleteModalOpen(false)} 
              className="modal-button cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </header>
  );
};

export default Header;
